import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useFirestoreDocument } from '../hooks/useFirestoreDocument';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useRecordClassifications } from '../hooks/useRecordClassifications';
import type { ClassificationEntry, ClassificationHierarchy } from '../hooks/useRecordClassifications';
import { useFunctionClassifications } from '../hooks/useFunctionClassifications';
import type { FunctionClassificationHierarchy } from '../hooks/useFunctionClassifications';
import { apiFetch } from '../lib/apiFetch';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  Loader2,
  ShieldAlert,
  ChevronRight,
  RefreshCw,
  Mail,
  Database,
  FolderTree,
  XCircle,
  GitCommit,
  Link,
  Info,
  ChevronDown,
  ChevronUp,
  Clock,
  ShieldCheck,
  Search,
  ArrowLeft,
  ExternalLink
} from 'lucide-react';
import Tooltip from '../components/Tooltip';
import AppNavActions from '../components/AppNavActions';
import FeedbackThumbs from '../components/FeedbackThumbs';
import type { FeedbackField } from '../components/FeedbackThumbs';
import FileDropZone from '../components/FileDropZone';
import AfterReviewModal from '../components/AfterReviewModal';
import IngestionActivityCard from '../components/IngestionActivityCard';
import { formatRejectedFilesMessage } from '../constants/upload';
import ThumbnailPreview from '../components/ThumbnailPreview';
import TimeAgo from '../components/TimeAgo';
import { formatBytes, getFileTypeLabel, getFileIcon, formatRetentionPeriod } from '../utils/fileUtils';
import {
  GeographicalContextEditable,
  GeographicalContextAddressesProcessing,
  GeographicalContextValuationsParcels,
} from '../components/GeographicalContextPanel';
import { PII_SENSITIVITY_OPTIONS, formatIscLevelName } from '../constants/record';
import { SHOW_AUTO_PROCESS_CONTROLS, SHOW_RETENTION_AND_DISPOSAL } from '../constants/feature_flags';

// --- Interfaces ---

/** Populated by the pipeline when status is `failed` (see document_processor). */
interface IngestionProcessError {
  code?: string;
  message?: string;
  severity?: string;
}

interface IngestionProcess {
  id: string;
  status: string; // initiated, in_progress, completed, failed
  initiated_at: any; // Firestore Timestamp
  completed_at?: any;
  failed_at?: any;
  record_id?: string;
  errors?: IngestionProcessError[];
  steps?: {
    current_step: string;
    all_steps: Array<{
      step: string;
      started_at: any;
    }>;
  };
  file: {
    name: string;
    size: number;
    content_type: string;
    checksum: string;
  };
  processing: {
    suggested_title?: string;
    content_summary?: string;
    personal_information_detection?: {
      detected: boolean;
      sensitivity_level: string;
      confidence: string;
      reason: string;
      entity_recognition?: Array<{
        entity: string;
        sensitivity_level: string;
        category: string;
        context_snippet: string;
      }>;
      overridden?: boolean;
    };
    maori_data_sovereignty_detection?: {
      detected: boolean;
      category: string;
      confidence: string;
      reason: string;
      overridden?: boolean;
    };
    potential_record_classifications?: Array<{
      classification_id: string;
      disposal_recommendation: string;
      level_1: { code: string; description: string; name: string };
      level_2: { code: string; name: string };
      level_3: { code: string; description: string; name: string };
      minimum_retention_period: string;
      retention_period_start_condition: string;
      note: string;
      similarity_distance: number;
    }>;
    suggested_record_classification_id?: string;
    potential_function_classifications?: Array<{
      classification_id: string;
      domain: { domain_name: string; domain_key: string; domain_description: string };
      subdomain: { subdomain_name: string; subdomain_key: string };
      function: { function_name: string; function_key: string };
      similarity_distance: number;
    }>;
    suggested_function_classification_id?: string;
    information_security_classification_detection?: {
      level: string;
      reason: string;
      confidence: string;
      overridden?: boolean;
    };
    immutability_detection?: {
      final: boolean;
      reason: string;
      confidence: string;
      overridden?: boolean;
    };
    geographical_context?: {
      suggested_address?: string;
      parsed_addresses?: Array<{
        formatted_address?: string;
        street_number?: number;
        street_name?: string;
        street_type?: string;
        suburb?: string;
        town_or_city?: string;
        latitude?: number;
        longitude?: number;
      }>;
      parsed_street?: {
        formatted_street?: string;
        street_name?: string;
        street_type?: string;
        town_or_city?: string;
      } | null;
      validated_addresses?: Array<{
        formatted_address?: string;
        latitude?: number;
        longitude?: number;
      }>;
      validated_street?: {
        formatted_street?: string;
      } | null;
      rating_valuations?: Array<{
        valuation_reference_number?: string;
        assessment_id?: string;
        image_url?: string;
      }>;
      parcels?: Array<{
        parcel_id?: string;
        status?: string;
        appellation?: string;
      }>;
    };
    potential_previous_versions?: Array<{
      record_id: string;
      name: string;
      title?: string;
      content_summary?: string;
      record_classification_id?: string;
      function_classification_id?: string;
      similarity_distance: number;
      confidence?: 'high' | 'medium' | 'low';
    }>;
    suggested_previous_version_id?: string;
    duplicate?: boolean;
    thumbnail?: {
      bucket: string;
      prefix: string;
      uri: string;
      content_type: string;
      size: number;
    };
  };
  metadata: {
    created: any;
    modified: any;
  };
}

/**
 * --- TYPES & INTERFACES ---
 */

type ViewState = 'IDLE' | 'PROCESSING' | 'REVIEW' | 'SUCCESS' | 'ERROR';

type PreviousVersionCandidate = NonNullable<
  IngestionProcess['processing']['potential_previous_versions']
>[number];

type ManualFunctionClassification = {
  classification_id: string;
  domain: { domain_name: string; domain_key: string; domain_description: string };
  subdomain: { subdomain_name: string; subdomain_key: string };
  function: { function_name: string; function_key: string };
  similarity_distance: number;
};

/** Find a record classification entry by id in the taxonomy hierarchy. */
function findRecordClassificationEntry(
  hierarchy: ClassificationHierarchy,
  classificationId: string,
): ClassificationEntry | null {
  for (const level2 of Object.values(hierarchy)) {
    for (const entries of Object.values(level2)) {
      const found = entries.find((entry) => entry.classification_id === classificationId);
      if (found) return found;
    }
  }
  return null;
}

/** Build the manual function-classification panel state from a hierarchy id. */
function findFunctionClassificationEntry(
  hierarchy: FunctionClassificationHierarchy,
  classificationId: string,
): ManualFunctionClassification | null {
  for (const [domainKey, domainData] of Object.entries(hierarchy)) {
    for (const subdomain of domainData.subdomains) {
      const fn = subdomain.functions.find((f) => f.classification_id === classificationId);
      if (!fn) continue;
      return {
        classification_id: classificationId,
        domain: {
          domain_name: domainData.domain,
          domain_key: domainKey,
          domain_description: domainData.description,
        },
        subdomain: {
          subdomain_name: subdomain.name,
          subdomain_key: subdomain.key,
        },
        function: {
          function_name: fn.name,
          function_key: fn.key,
        },
        similarity_distance: 0,
      };
    }
  }
  return null;
}

/**
 * --- CONSTANTS ---
 */

const BRAND_BG = "bg-emerald-800";
const BRAND_TEXT = "text-emerald-800";
/**
 * --- SUB-COMPONENTS ---
 */

const ToggleSwitch = ({ enabled, onChange, disabled = false }: { enabled: boolean, onChange: (val: boolean) => void, disabled?: boolean }) => (
  <button
    onClick={(e) => { e.stopPropagation(); !disabled && onChange(!enabled); }}
    className={`
      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none
      ${enabled ? BRAND_BG : 'bg-gray-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const ConfidencePill = ({ score, hidden }: { score: string, hidden?: boolean }) => {
  if (hidden || !score) return null;

  let color = 'bg-gray-100 text-gray-600';
  const s = score.toLowerCase();

  if (s === 'high') color = 'bg-emerald-100 text-emerald-800';
  else if (s === 'medium') color = 'bg-amber-100 text-amber-800';

  const label = score.charAt(0).toUpperCase() + score.slice(1);

  return (
    <Tooltip content="AI confidence level">
      <span className={`text-xs px-2 py-0.5 rounded-md font-mono font-medium ${color}`}>{label}</span>
    </Tooltip>
  );
};

// Step duration helper
const StepDuration = ({ startedAt, nextStepStartedAt, isLast, processStatus, completedAt, failedAt, processModifiedAt }: { startedAt: any, nextStepStartedAt?: any, isLast: boolean, processStatus?: string, completedAt?: any, failedAt?: any, processModifiedAt?: any }) => {
  const [duration, setDuration] = useState('');

  useEffect(() => {
    const start = startedAt?.toDate ? startedAt.toDate() : new Date(startedAt);
    let end: Date | null = null;

    if (nextStepStartedAt) {
      end = nextStepStartedAt.toDate ? nextStepStartedAt.toDate() : new Date(nextStepStartedAt);
    } else if (processStatus && ['completed', 'confirmed', 'discarded', 'failed'].includes(processStatus)) {
      if (completedAt) {
        end = completedAt.toDate ? completedAt.toDate() : new Date(completedAt);
      } else if (failedAt) {
        end = failedAt.toDate ? failedAt.toDate() : new Date(failedAt);
      } else if (processModifiedAt) {
        end = processModifiedAt.toDate ? processModifiedAt.toDate() : new Date(processModifiedAt);
      }
    }

    const update = () => {
      const now = end || new Date();
      const diff = Math.max(0, Math.floor((now.getTime() - start.getTime()) / 1000));

      if (diff < 60) {
        setDuration(`${diff}s`);
      } else if (diff < 3600) {
        const m = Math.floor(diff / 60);
        const s = diff % 60;
        setDuration(`${m}m ${s}s`);
      } else {
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        setDuration(`${h}h ${m}m`);
      }
    };

    update();

    if (!end && isLast) {
      const interval = setInterval(update, 1000);
      return () => clearInterval(interval);
    }
  }, [startedAt, nextStepStartedAt, isLast, processStatus, completedAt, failedAt, processModifiedAt]);

  return <span>{duration}</span>;
};

/** AI suggestions a user can single out when giving feedback; keys match `processing` fields. */
function buildUploadFeedbackFields(
  processing: IngestionProcess['processing'] | undefined,
  previousVersionSelectedByUser = false,
): FeedbackField[] {
  if (!processing) return [];

  const fields: FeedbackField[] = [];
  const add = (key: string, label: string, value: string | null | undefined, meta?: FeedbackField['meta']) => {
    if (value) fields.push({ key, label, value, group: 'AI suggestions', meta });
  };

  add('suggested_title', 'Record Title', processing.suggested_title);
  add('content_summary', 'AI Summary', processing.content_summary);

  const recordClassification = processing.potential_record_classifications?.find(
    (c) => c.classification_id === processing.suggested_record_classification_id
  );
  add(
    'suggested_record_classification_id',
    'Record Classification',
    recordClassification &&
      [recordClassification.level_1?.name, recordClassification.level_2?.name, recordClassification.level_3?.name]
        .filter(Boolean)
        .join(' > '),
    recordClassification && { classification_id: recordClassification.classification_id }
  );

  const functionClassification = processing.potential_function_classifications?.find(
    (c) => c.classification_id === processing.suggested_function_classification_id
  );
  add(
    'suggested_function_classification_id',
    'Business Function',
    functionClassification &&
      [
        functionClassification.domain?.domain_name,
        functionClassification.subdomain?.subdomain_name,
        functionClassification.function?.function_name,
      ]
        .filter(Boolean)
        .join(' > '),
    functionClassification && { classification_id: functionClassification.classification_id }
  );

  const pii = processing.personal_information_detection;
  add(
    'personal_information_detection',
    'Personal Information',
    pii && (pii.detected ? `Detected${pii.sensitivity_level ? ` (${pii.sensitivity_level})` : ''}` : 'Not detected')
  );

  const maori = processing.maori_data_sovereignty_detection;
  add(
    'maori_data_sovereignty_detection',
    'Māori Data',
    maori && (maori.detected ? `Detected${maori.category ? ` (${maori.category})` : ''}` : 'Not detected')
  );

  const isc = processing.information_security_classification_detection;
  add('information_security_classification_detection', 'Information Security Classification', isc?.level && formatIscLevelName(isc.level));

  const immutability = processing.immutability_detection;
  add('immutability_detection', 'Mark as Final Record', immutability && (immutability.final ? 'Final' : 'Not final'));

  add('geographical_context.suggested_address', 'Geographical Context', processing.geographical_context?.suggested_address);

  const linked = processing.potential_previous_versions?.find(
    (version) => version.record_id === processing.suggested_previous_version_id,
  );
  const linkSource = previousVersionSelectedByUser ? 'selected' : 'auto-linked';
  fields.push({
    key: 'suggested_previous_version_id',
    label: 'Potential Previous Version',
    value: linked ? `${linked.name} (${linkSource})` : 'No version linked',
    group: 'AI suggestions',
    ...(linked ? { meta: { record_id: linked.record_id, link_source: linkSource } } : {}),
  });

  return fields;
}

function formatIngestionErrorSummary(err: IngestionProcessError): string {
  const code = err.code?.trim();
  const msg = err.message?.trim();
  if (code && msg) return `${code}: ${msg}`;
  return msg || code || '';
}

/** Payload fields the confirm response can name. Editing one clears its message. */
const CONFIRM_FIELD_FOR_PATH: Record<string, string> = {
  'processing.suggested_title': 'suggested_title',
  'processing.content_summary': 'content_summary',
  'processing.suggested_record_classification_id': 'suggested_record_classification_id',
  'processing.suggested_function_classification_id': 'suggested_function_classification_id',
};

/** Read the field messages from a confirm error. The messages are the API's, not rules. */
function confirmFieldErrorsByName(body: unknown): Record<string, string> {
  if (!body || typeof body !== 'object' || !('fields' in body)) return {};
  const fields = (body as { fields?: unknown }).fields;
  if (!Array.isArray(fields)) return {};

  const byName: Record<string, string> = {};
  for (const entry of fields) {
    if (!entry || typeof entry !== 'object') continue;
    const field = (entry as { field?: unknown }).field;
    const message = (entry as { message?: unknown }).message;
    if (typeof field === 'string' && field && typeof message === 'string' && message) {
      byName[field] = message;
    }
  }
  return byName;
}

interface AfterReviewHandoff {
  recordId: string | null;
  discarded: boolean;
}

/** Survives the upload page remounting on navigation. Cleared once that page has rendered the modal. */
let pendingAfterReview: AfterReviewHandoff | null = null;

/** Read the finished review from router state. Undefined when this navigation did not finish one. */
function afterReviewFromState(state: unknown): AfterReviewHandoff | undefined {
  if (!state || typeof state !== 'object' || !('afterReview' in state)) return undefined;
  const value = (state as { afterReview: unknown }).afterReview;
  if (!value || typeof value !== 'object') return undefined;
  const recordId = (value as { recordId?: unknown }).recordId;
  const discarded = (value as { discarded?: unknown }).discarded;
  return {
    recordId: typeof recordId === 'string' ? recordId : null,
    discarded: discarded === true,
  };
}

/**
 * Keep a finished review across the jump to the upload page.
 * That page mounts fresh on the navigation, so the result is held until it has rendered.
 */
function claimAfterReview(state: unknown): AfterReviewHandoff | null {
  const fromLocation = afterReviewFromState(state);
  if (fromLocation) {
    pendingAfterReview = fromLocation;
  }
  return pendingAfterReview;
}

function clearAfterReview(): void {
  pendingAfterReview = null;
}

/**
 * --- MAIN APP COMPONENT ---
 */

export default function ManualUpload() {
  const { user } = useAuth();
  const { id: routeProcessId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [viewState, setViewState] = useState<ViewState>(routeProcessId ? 'REVIEW' : 'IDLE');
  const [afterReview, setAfterReview] = useState<AfterReviewHandoff | null>(() =>
    routeProcessId ? null : claimAfterReview(location.state)
  );
  const [activeItem, setActiveItem] = useState<IngestionProcess | null>(
    routeProcessId ? ({ id: routeProcessId, file: {}, processing: {}, metadata: {} } as IngestionProcess) : null
  );
  const [recentActivity, setRecentActivity] = useState<IngestionProcess[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Settings state
  const [isAutoProcess, setIsAutoProcess] = useState(false);
  const [emailNotification, setEmailNotification] = useState(false);

  // UI Helper state
  const [showTaxonomyPicker, setShowTaxonomyPicker] = useState(false);
  const [pickerLevel, setPickerLevel] = useState<1 | 2 | 3>(1);
  const [selectedLevel1, setSelectedLevel1] = useState<string | null>(null);
  const [selectedLevel2, setSelectedLevel2] = useState<string | null>(null);
  const [classificationSearch, setClassificationSearch] = useState('');
  const [stepsExpanded, setStepsExpanded] = useState(false);
  const [showWeakerVersionMatches, setShowWeakerVersionMatches] = useState(false);
  const [showUnlinkedPreviousVersionConfirm, setShowUnlinkedPreviousVersionConfirm] = useState(false);
  const unlinkedPreviousVersionDialogRef = useRef<HTMLDivElement>(null);
  const previousVersionTouchedRef = useRef(false);
  const versionFieldsAdoptedForProcessRef = useRef<string | null>(null);
  const previousVersionProcessIdRef = useRef(activeItem?.id);
  const [showFunctionPicker, setShowFunctionPicker] = useState(false);
  const [functionPickerLevel, setFunctionPickerLevel] = useState<1 | 2 | 3>(1);
  const [selectedFunctionDomain, setSelectedFunctionDomain] = useState<string | null>(null);
  const [selectedFunctionSubdomain, setSelectedFunctionSubdomain] = useState<string | null>(null);
  const [functionSearch, setFunctionSearch] = useState('');

  const { hierarchy: classificationHierarchy, loading: classificationsLoading } = useRecordClassifications();
  const { hierarchy: functionHierarchy, loading: functionClassificationsLoading } = useFunctionClassifications();

  const [manualClassification, setManualClassification] = useState<ClassificationEntry & { similarity_distance: number } | null>(null);

  const selectedClassification = useMemo(() => {
    if (manualClassification) return manualClassification;
    const candidates = activeItem?.processing?.potential_record_classifications;
    const selectedId = activeItem?.processing?.suggested_record_classification_id;
    if (!candidates || !selectedId) return null;
    return candidates.find(c => c.classification_id === selectedId) ?? null;
  }, [manualClassification, activeItem?.processing?.potential_record_classifications, activeItem?.processing?.suggested_record_classification_id]);

  const [manualFunctionClassification, setManualFunctionClassification] =
    useState<ManualFunctionClassification | null>(null);

  const selectedFunctionClassification = useMemo(() => {
    if (manualFunctionClassification) return manualFunctionClassification;
    const candidates = activeItem?.processing?.potential_function_classifications;
    const selectedId = activeItem?.processing?.suggested_function_classification_id;
    if (!candidates || !selectedId) return null;
    return candidates.find(c => c.classification_id === selectedId) ?? null;
  }, [manualFunctionClassification, activeItem?.processing?.potential_function_classifications, activeItem?.processing?.suggested_function_classification_id]);

  if (previousVersionProcessIdRef.current !== activeItem?.id) {
    previousVersionProcessIdRef.current = activeItem?.id;
    previousVersionTouchedRef.current = false;
    versionFieldsAdoptedForProcessRef.current = null;
  }

  const uploadFeedbackFields = useMemo(
    () => buildUploadFeedbackFields(activeItem?.processing, previousVersionTouchedRef.current),
    [activeItem?.processing]
  );

  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  useFocusTrap(unlinkedPreviousVersionDialogRef, {
    enabled: showUnlinkedPreviousVersionConfirm,
    onEscape: () => {
      if (!isSubmitting) setShowUnlinkedPreviousVersionConfirm(false);
    },
  });
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);

  // Inline error banner state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmFieldErrors, setConfirmFieldErrors] = useState<Record<string, string>>({});

  // -- REAL-TIME RECENT ACTIVITY --
  useEffect(() => {
    const userEmail = user?.email;
    if (!userEmail) {
      setRecentActivity([]);
      setIsLoadingHistory(false);
      return;
    }

    setIsLoadingHistory(true);
    const q = query(
      collection(db, "ingestion_process"),
      where("initiated_by", "==", userEmail),
      orderBy("initiated_at", "desc"),
      limit(10)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const mappedItems: IngestionProcess[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure dates are Date objects
          initiated_at: data.initiated_at?.toDate ? data.initiated_at.toDate() : (data.initiated_at ? new Date(data.initiated_at) : null),
          completed_at: data.completed_at?.toDate ? data.completed_at.toDate() : (data.completed_at ? new Date(data.completed_at) : undefined),
          failed_at: data.failed_at?.toDate ? data.failed_at.toDate() : (data.failed_at ? new Date(data.failed_at) : undefined),
          metadata: {
            ...data.metadata,
            created: data.metadata?.created?.toDate ? data.metadata.created.toDate() : (data.metadata?.created ? new Date(data.metadata.created) : null),
            modified: data.metadata?.modified?.toDate ? data.metadata.modified.toDate() : (data.metadata?.modified ? new Date(data.metadata.modified) : null)
          }
        } as IngestionProcess;
      });
      setRecentActivity(mappedItems);
      setIsLoadingHistory(false);
    }, (error) => {
      console.error("Failed to subscribe to recent activity:", error);
      setIsLoadingHistory(false);
    });

    return () => unsubscribe();
  }, [user?.email]);

  useEffect(() => {
    if (routeProcessId && routeProcessId !== activeItem?.id) {
      setActiveItem({ id: routeProcessId, file: {}, processing: {}, metadata: {} } as IngestionProcess);
      setViewState('REVIEW');
    }
  }, [routeProcessId, activeItem?.id]);

  useEffect(() => {
    if (routeProcessId || !afterReview) return;
    if (afterReviewFromState(location.state)) {
      navigate('/upload', { replace: true, state: null });
    }
    window.setTimeout(clearAfterReview, 0);
  }, [routeProcessId, afterReview, location.state, navigate]);

  // -- REAL-TIME DATA SYNC --
  const { ingestionProcess } = useFirestoreDocument(activeItem?.id || null);

  useEffect(() => {
    if (!activeItem || !ingestionProcess) return;

    // Direct mapping with Timestamp conversion
    // We assume ingestionProcess matches the IngestionProcess interface structure (mostly)
    const newData = { ...ingestionProcess };

    // Helper to convert Timestamp to Date
    const toDate = (val: any) => (val?.toDate ? val.toDate() : (val ? new Date(val) : null));

    if (newData.metadata) {
      if (newData.metadata.created) newData.metadata.created = toDate(newData.metadata.created);
      if (newData.metadata.modified) newData.metadata.modified = toDate(newData.metadata.modified);
    }
    if (newData.initiated_at) newData.initiated_at = toDate(newData.initiated_at);
    if (newData.completed_at) newData.completed_at = toDate(newData.completed_at);
    if (newData.failed_at) newData.failed_at = toDate(newData.failed_at);

    setActiveItem(prev => {
      if (!prev) return prev;

      const merged = {
        ...prev,
        ...newData,
        id: prev.id,
      };

      if (prev.processing?.suggested_record_classification_id) {
        merged.processing = {
          ...merged.processing,
          suggested_record_classification_id: prev.processing.suggested_record_classification_id,
        };
      }

      if (prev.processing?.suggested_function_classification_id) {
        merged.processing = {
          ...merged.processing,
          suggested_function_classification_id: prev.processing.suggested_function_classification_id,
        };
      }

      if (previousVersionTouchedRef.current) {
        merged.processing = {
          ...merged.processing,
          suggested_previous_version_id: prev.processing?.suggested_previous_version_id,
        };
      }

      // Keep adopted title/summary while a previous version is linked (manual or auto).
      if (
        previousVersionTouchedRef.current ||
        prev.processing?.suggested_previous_version_id
      ) {
        merged.processing = {
          ...merged.processing,
          suggested_title: prev.processing?.suggested_title,
          content_summary: prev.processing?.content_summary,
        };
      }

      return merged;
    });

  }, [ingestionProcess]);

  useEffect(() => {
    previousVersionTouchedRef.current = false;
    versionFieldsAdoptedForProcessRef.current = null;
    setShowWeakerVersionMatches(false);
    setShowUnlinkedPreviousVersionConfirm(false);
    setConfirmFieldErrors({});
    setManualClassification(null);
    setManualFunctionClassification(null);
  }, [activeItem?.id]);

  // -- HANDLERS --

  const extractErrorMessage = async (response: Response, fallback: string): Promise<string> => {
    try {
      const text = await response.text();
      const json = JSON.parse(text);
      return json.error || json.message || json.detail || fallback;
    } catch {
      return fallback;
    }
  };

  const startUpload = async (file: File, opts?: { skipReview?: boolean }) => {
    setErrorMessage(null);
    // 1. Initial temporary state
    const skipReview = opts?.skipReview ?? false;
    const autoProcessEnabled = isAutoProcess;
    const showReview = !skipReview && !autoProcessEnabled;

    const tempId = `temp-${Date.now()}`;
    // Initialize with safe defaults for UI rendering
    const tempItem: IngestionProcess = {
      id: tempId,
      status: 'Initializing...',
      initiated_at: new Date(),
      file: {
        name: file.name,
        size: file.size,
        content_type: file.type || 'application/octet-stream',
        checksum: 'Pending'
      },
      processing: {
        content_summary: '',
        personal_information_detection: { detected: false, sensitivity_level: 'None', confidence: 'low', reason: '' },
        maori_data_sovereignty_detection: { detected: false, category: '', confidence: 'low', reason: '' },
        potential_function_classifications: [],
        suggested_function_classification_id: undefined,
        information_security_classification_detection: { level: 'in-confidence', reason: '', confidence: 'low' },
        immutability_detection: { final: false, reason: '', confidence: 'low' },
        geographical_context: { suggested_address: '', parsed_addresses: [], parsed_street: null, validated_addresses: [], validated_street: null, rating_valuations: [], parcels: [] },
        potential_previous_versions: []
      },
      metadata: {
        created: new Date(),
        modified: new Date()
      }
    };

    if (showReview) {
      setActiveItem(tempItem);
      setViewState('REVIEW');
    }

    try {
      // 2. Call API to initiate upload
      const initResponse = await apiFetch('/api/v1/processing/init', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          files: [{
            name: file.name,
            type: file.type,
            size: file.size
          }],
          auto_process: autoProcessEnabled
        })
      });

      if (!initResponse.ok) {
        const msg = await extractErrorMessage(initResponse, `Upload initiation failed (${initResponse.status})`);
        throw new Error(msg);
      }

      const initData = await initResponse.json();
      const processResource = initData._embedded?.ingestion_processes?.[0];
      // The API reports per-file rejections (too large, duplicate, …) with a 200
      // and an `error` on the file entry rather than a non-OK status.
      if (!processResource || processResource.error || !processResource.uploadUrl) {
        throw new Error(processResource?.error || 'Upload initiation failed');
      }
      const { id, uploadUrl, contentType } = processResource;
      // 3. Update Active Item with real ID

      if (showReview) {
        setActiveItem(prev => {
          if (!prev) return null;
          return {
            ...prev,
            id: id,
            status: 'Uploading...',
            file: { ...prev.file, content_type: contentType }
          };
        });
        navigate(`/processing/${id}`, { state: { from: '/upload' } });
      }

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
          'X-Goog-Meta-pncc-processing-id': id
        },
        body: file
      });

      if (!uploadResponse.ok) {
        const msg = await extractErrorMessage(uploadResponse, `File upload failed (${uploadResponse.status})`);
        throw new Error(msg);
      }

      if (autoProcessEnabled) {
        addToast(`"${file.name}" uploaded — it will be auto-confirmed once processing completes.`, 'info');
      } else if (skipReview) {
        addToast(`"${file.name}" uploaded — ready for review once processing completes.`, 'info');
      }

    } catch (error: any) {
      console.error("Upload failed", error);
      if (skipReview) {
        const reason = error?.message ? ` ${error.message}` : '';
        addToast(`"${file.name}" failed to upload.${reason}`, 'error');
        return;
      }
      if (showReview) {
        setViewState('ERROR');
      }
      setErrorMessage(error?.message || "Failed to upload file.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Open the first file for review; upload the rest without opening the review screen.
  const startBatch = (files: File[]) => {
    if (files.length === 0) return;
    startUpload(files[0]);
    files.slice(1).forEach(f => startUpload(f, { skipReview: true }));
  };



  const handleReset = () => {
    setManualClassification(null);
    setManualFunctionClassification(null);
    setActiveItem(null);
    setViewState('IDLE');
    setStepsExpanded(false);
    setShowWeakerVersionMatches(false);
    setShowUnlinkedPreviousVersionConfirm(false);
    previousVersionTouchedRef.current = false;
    setErrorMessage(null);
    setConfirmFieldErrors({});
    if (!routeProcessId) return;

    const from = (location.state as { from?: string } | null)?.from;
    if (from) {
      navigate(-1);
    } else {
      navigate('/upload', { replace: true });
    }
  };

  const handleRetry = () => {
    handleReset();
  };

  const submitConfirm = async () => {
    if (!activeItem || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);
    setConfirmFieldErrors({});
    let pointedAtField = false;
    try {
      const url = (activeItem as any).confirmUrl || `/api/v1/processing/${activeItem.id}/confirm`;
      const response = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          suggested_title: activeItem.processing?.suggested_title,
          content_summary: activeItem.processing?.content_summary,
          suggested_record_classification_id: activeItem.processing?.suggested_record_classification_id,
          suggested_previous_version_id: activeItem.processing?.suggested_previous_version_id ?? null,
          suggested_function_classification_id: activeItem.processing?.suggested_function_classification_id ?? null,
          geographical_context: {
            suggested_address: activeItem.processing?.geographical_context?.suggested_address,
          },
          immutability_detection: {
            final: activeItem.processing?.immutability_detection?.final,
          },
          personal_information_detection: {
            detected: activeItem.processing?.personal_information_detection?.detected,
            sensitivity_level: activeItem.processing?.personal_information_detection?.detected
              ? (activeItem.processing?.personal_information_detection?.sensitivity_level || 'standard')
              : null,
          },
          maori_data_sovereignty_detection: {
            detected: activeItem.processing?.maori_data_sovereignty_detection?.detected,
          },
          information_security_classification_detection: {
            level: activeItem.processing?.information_security_classification_detection?.level,
          },
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null) as { error?: unknown } | null;
        const fieldErrors = confirmFieldErrorsByName(body);
        const message = typeof body?.error === 'string' && body.error
          ? body.error
          : `Confirm failed (${response.status})`;
        setConfirmFieldErrors(fieldErrors);
        const firstField = Object.keys(fieldErrors)[0];
        const fieldElement = firstField
          ? document.getElementById(`confirm-field-${firstField}`)
          : null;
        if (fieldElement) {
          pointedAtField = true;
          fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        throw new Error(message);
      }

      const confirmed = (await response.json().catch(() => null)) as {
        _embedded?: { record?: { id?: unknown } };
      } | null;
      const recordId =
        typeof confirmed?._embedded?.record?.id === 'string'
          ? confirmed._embedded.record.id
          : null;

      navigate('/upload', { replace: true, state: { afterReview: { recordId, discarded: false } } });

    } catch (err: any) {
      console.error(err);
      setErrorMessage(err?.message || "Failed to confirm record.");
      if (!pointedAtField) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = () => {
    if (!activeItem || isSubmitting) return;

    const hasHighConfidencePreviousVersion = (activeItem.processing?.potential_previous_versions ?? []).some(
      (version) => version.confidence === 'high'
    );
    if (!activeItem.processing?.suggested_previous_version_id && hasHighConfidencePreviousVersion) {
      setShowUnlinkedPreviousVersionConfirm(true);
      return;
    }

    submitConfirm();
  };

  const handleConfirmWithoutPreviousVersion = () => {
    setShowUnlinkedPreviousVersionConfirm(false);
    submitConfirm();
  };

  const handleDiscard = async () => {
    if (!activeItem || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const url = (activeItem as any).discardUrl || `/api/v1/processing/${activeItem.id}/discard`;
      const response = await apiFetch(url, {
        method: 'POST'
      });
      if (!response.ok) {
        const msg = await extractErrorMessage(response, `Discard failed (${response.status})`);
        throw new Error(msg);
      }

      navigate('/upload', {
        replace: true,
        state: { afterReview: { recordId: null, discarded: true } },
      });

    } catch (err: any) {
      console.error(err);
      setErrorMessage(err?.message || "Failed to discard record.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generic update handler for deep nested fields
  const handleUpdateField = (path: string, value: any) => {
    const confirmedField = CONFIRM_FIELD_FOR_PATH[path];
    if (confirmedField) {
      setConfirmFieldErrors(prev => {
        if (!(confirmedField in prev)) return prev;
        const next = { ...prev };
        delete next[confirmedField];
        return next;
      });
    }
    setActiveItem(prevItem => {
      if (!prevItem) return prevItem;

      // Shallow clone the top level
      const newItem = { ...prevItem } as any;

      // Navigate and safely copy paths to avoid mutating state or destroying Dates via JSON.parse
      const parts = path.split('.');
      let current = newItem;
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = { ...current[parts[i]] }; // shadow clone the next level
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;

      return newItem;
    });
  };

  /** Adopt title, summary, and classifications from a linked previous version into local review state. */
  const applyPreviousVersionFields = (version: PreviousVersionCandidate) => {
    const linkedTitle = version.title?.trim();
    const linkedSummary = version.content_summary?.trim();
    const linkedRecordClassificationId = version.record_classification_id?.trim();
    const linkedFunctionClassificationId = version.function_classification_id?.trim();

    setConfirmFieldErrors((prev) => {
      const next = { ...prev };
      delete next.suggested_title;
      delete next.content_summary;
      delete next.suggested_record_classification_id;
      delete next.suggested_function_classification_id;
      return next;
    });

    setActiveItem((prevItem) => {
      if (!prevItem) return prevItem;
      return {
        ...prevItem,
        processing: {
          ...prevItem.processing,
          suggested_previous_version_id: version.record_id,
          ...(linkedTitle ? { suggested_title: linkedTitle } : {}),
          ...(linkedSummary ? { content_summary: linkedSummary } : {}),
          ...(linkedRecordClassificationId
            ? { suggested_record_classification_id: linkedRecordClassificationId }
            : {}),
          ...(linkedFunctionClassificationId
            ? { suggested_function_classification_id: linkedFunctionClassificationId }
            : {}),
        },
      };
    });

    if (linkedRecordClassificationId) {
      const fromHierarchy = findRecordClassificationEntry(
        classificationHierarchy,
        linkedRecordClassificationId,
      );
      const fromPotentials = activeItem?.processing?.potential_record_classifications?.find(
        (potential) => potential.classification_id === linkedRecordClassificationId,
      );
      if (fromHierarchy) {
        setManualClassification({ ...fromHierarchy, similarity_distance: 0 });
      } else if (fromPotentials) {
        setManualClassification({
          classification_id: fromPotentials.classification_id,
          level_1: fromPotentials.level_1,
          level_2: fromPotentials.level_2,
          level_3: fromPotentials.level_3,
          disposal_recommendation: fromPotentials.disposal_recommendation,
          minimum_retention_period: fromPotentials.minimum_retention_period,
          retention_period_start_condition: fromPotentials.retention_period_start_condition,
          note: fromPotentials.note,
          similarity_distance: fromPotentials.similarity_distance,
        });
      } else {
        setManualClassification(null);
      }
    }

    if (linkedFunctionClassificationId) {
      const fromHierarchy = findFunctionClassificationEntry(
        functionHierarchy,
        linkedFunctionClassificationId,
      );
      const fromPotentials = activeItem?.processing?.potential_function_classifications?.find(
        (potential) => potential.classification_id === linkedFunctionClassificationId,
      );
      if (fromHierarchy) {
        setManualFunctionClassification(fromHierarchy);
      } else if (fromPotentials) {
        setManualFunctionClassification({
          classification_id: fromPotentials.classification_id,
          domain: fromPotentials.domain,
          subdomain: fromPotentials.subdomain,
          function: fromPotentials.function,
          similarity_distance: fromPotentials.similarity_distance,
        });
      } else {
        setManualFunctionClassification(null);
      }
    }
  };

  const handleMarkAsPreviousVersion = (version: PreviousVersionCandidate) => {
    if (!activeItem) return;
    previousVersionTouchedRef.current = true;
    versionFieldsAdoptedForProcessRef.current = activeItem.id;
    applyPreviousVersionFields(version);
  };

  const handleClearPreviousVersionLink = () => {
    if (!activeItem) return;
    previousVersionTouchedRef.current = true;
    versionFieldsAdoptedForProcessRef.current = activeItem.id;

    const original = ingestionProcess?.processing;
    handleUpdateField('processing.suggested_previous_version_id', null);
    handleUpdateField(
      'processing.suggested_title',
      original?.suggested_title ?? '',
    );
    handleUpdateField(
      'processing.content_summary',
      original?.content_summary ?? '',
    );
    handleUpdateField(
      'processing.suggested_record_classification_id',
      original?.suggested_record_classification_id,
    );
    handleUpdateField(
      'processing.suggested_function_classification_id',
      original?.suggested_function_classification_id,
    );
    setManualClassification(null);
    setManualFunctionClassification(null);
  };

  // Auto-link: adopt title, summary, and classifications from a backend-selected previous version.
  useEffect(() => {
    const processId = activeItem?.id;
    if (!processId) return;
    if (previousVersionTouchedRef.current) return;
    if (versionFieldsAdoptedForProcessRef.current === processId) return;

    const linkedId = activeItem.processing?.suggested_previous_version_id;
    if (!linkedId) return;

    const version = activeItem.processing?.potential_previous_versions?.find(
      (candidate) => candidate.record_id === linkedId,
    );
    if (!version) return;

    const needsRecord = Boolean(version.record_classification_id?.trim());
    const needsFunction = Boolean(version.function_classification_id?.trim());
    if (
      (needsRecord && classificationsLoading) ||
      (needsFunction && functionClassificationsLoading)
    ) {
      return;
    }

    versionFieldsAdoptedForProcessRef.current = processId;
    applyPreviousVersionFields(version);
    // applyPreviousVersionFields closes over current hierarchies; re-run when they load.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adopt once per process via ref
  }, [
    activeItem?.id,
    activeItem?.processing?.suggested_previous_version_id,
    activeItem?.processing?.potential_previous_versions,
    classificationsLoading,
    functionClassificationsLoading,
    classificationHierarchy,
    functionHierarchy,
  ]);

  const openClassificationPicker = (startAtLevel: 1 | 2 | 3) => {
    setClassificationSearch('');
    if (startAtLevel === 1) {
      setSelectedLevel1(null);
      setSelectedLevel2(null);
      setPickerLevel(1);
    } else if (startAtLevel === 2) {
      setSelectedLevel1(selectedClassification?.level_1?.name || null);
      setSelectedLevel2(null);
      setPickerLevel(2);
    } else {
      setSelectedLevel1(selectedClassification?.level_1?.name || null);
      setSelectedLevel2(selectedClassification?.level_2?.name || null);
      setPickerLevel(3);
    }
    setShowTaxonomyPicker(true);
  };

  const applyClassificationById = (candidateId: string) => {
    setManualClassification(null);
    handleUpdateField('processing.suggested_record_classification_id', candidateId);
    setShowTaxonomyPicker(false);
  };

  const applyClassificationFromTaxonomy = (entry: ClassificationEntry) => {
    setManualClassification({ ...entry, similarity_distance: 0 });
    handleUpdateField('processing.suggested_record_classification_id', entry.classification_id);
    setShowTaxonomyPicker(false);
  };

  const openFunctionPicker = () => {
    setFunctionSearch('');
    setSelectedFunctionDomain(null);
    setSelectedFunctionSubdomain(null);
    setFunctionPickerLevel(1);
    setShowFunctionPicker(true);
  };

  const openFunctionPickerAtSubdomain = () => {
    const domainKey = selectedFunctionClassification?.domain.domain_key;
    if (domainKey && functionHierarchy[domainKey]) {
      setSelectedFunctionDomain(domainKey);
      setSelectedFunctionSubdomain(null);
      setFunctionPickerLevel(2);
      setFunctionSearch('');
      setShowFunctionPicker(true);
    } else {
      openFunctionPicker();
    }
  };

  const openFunctionPickerAtFunction = () => {
    const domainKey = selectedFunctionClassification?.domain.domain_key;
    const subdomainKey = selectedFunctionClassification?.subdomain.subdomain_key;
    if (domainKey && subdomainKey && functionHierarchy[domainKey]?.subdomains.find(s => s.key === subdomainKey)) {
      setSelectedFunctionDomain(domainKey);
      setSelectedFunctionSubdomain(subdomainKey);
      setFunctionPickerLevel(3);
      setFunctionSearch('');
      setShowFunctionPicker(true);
    } else {
      openFunctionPickerAtSubdomain();
    }
  };

  const applyFunctionClassificationById = (candidateId: string) => {
    setManualFunctionClassification(null);
    handleUpdateField('processing.suggested_function_classification_id', candidateId);
    setShowFunctionPicker(false);
  };

  const applyFunctionClassificationFromTaxonomy = (domainKey: string, subdomainKey: string, functionKey: string) => {
    const domainData = functionHierarchy[domainKey];
    const subdomainData = domainData?.subdomains.find(s => s.key === subdomainKey);
    const functionData = subdomainData?.functions.find(f => f.key === functionKey);
    const classificationId = functionData?.classification_id ?? '';
    setManualFunctionClassification({
      classification_id: classificationId,
      domain: { domain_name: domainData?.domain ?? domainKey, domain_key: domainKey, domain_description: domainData?.description ?? '' },
      subdomain: { subdomain_name: subdomainData?.name ?? subdomainKey, subdomain_key: subdomainKey },
      function: { function_name: functionData?.name ?? functionKey, function_key: functionKey },
      similarity_distance: 0,
    });
    handleUpdateField('processing.suggested_function_classification_id', classificationId);
    setShowFunctionPicker(false);
  };

  // -- RENDER HELPERS --

  const renderIdleState = () => (
    <FileDropZone
      title="Upload Document"
      multiple
      onFiles={startBatch}
      onRejected={(rejected) => addToast(formatRejectedFilesMessage(rejected), 'error')}
      className="min-h-[500px]"
    >
      {SHOW_AUTO_PROCESS_CONTROLS && (
        <div className="flex items-center space-x-6 bg-gray-100 p-4 rounded-full">
          <div className="flex items-center space-x-3">
            <ToggleSwitch
              enabled={isAutoProcess}
              onChange={(val) => {
                setIsAutoProcess(val);
                if (!val) setEmailNotification(false);
              }}
            />
            <span className="text-sm font-medium text-gray-700">Auto-process if confident</span>
          </div>

          <div className="w-px h-4 bg-gray-300"></div>

          <div className="flex items-center space-x-3">
            <ToggleSwitch
              enabled={emailNotification}
              onChange={setEmailNotification}
              disabled={!isAutoProcess}
            />
            <div className={`flex items-center text-gray-700 text-sm ${!isAutoProcess ? 'opacity-40' : ''}`}>
              <Mail className="w-4 h-4 mr-2" />
              Notify me
            </div>
          </div>
        </div>
      )}
    </FileDropZone>
  );

  const renderProcessingState = () => {
    if (!activeItem) return null;
    return (
      <div className="max-w-3xl mx-auto mt-12">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="p-8 border-b border-gray-100 flex items-start justify-between bg-gray-50/50">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm flex items-center justify-center">
                {getFileIcon(activeItem.file.content_type)}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{activeItem.file.name}</h2>
                <p className="text-sm text-gray-500 font-mono mt-1">{formatBytes(activeItem.file.size)} • {activeItem.file.content_type}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-gray-900 animate-pulse">{activeItem.status}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold flex items-center justify-end gap-1">
                Updated <TimeAgo date={activeItem.metadata.modified} />
              </div>
            </div>
          </div>

          {/* Progress Visualization */}
          <div className="p-8 space-y-8">
            {/* Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm font-medium text-gray-600">
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                  {activeItem.status}
                </span>
              </div>
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden relative">
                <div className={`absolute inset-0 ${BRAND_BG} opacity-20`}></div>
                <div
                  className={`absolute inset-0 ${BRAND_BG} origin-left animate-[shimmer_2s_infinite] w-1/2`}
                  style={{
                    backgroundImage: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)'
                  }}
                ></div>
                {/* Indeterminate moving bar */}
                <div className={`h-full ${BRAND_BG} w-1/3 animate-[indeterminate_1.5s_infinite_linear]`}></div>
              </div>
            </div>

            {/* Real-time Discovery Feed */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* PII Card */}
              <div className={`p-4 rounded-xl border transition-all duration-500 ${activeItem.processing.personal_information_detection?.detected ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center gap-3 mb-2">
                  <ShieldAlert className={`w-5 h-5 ${activeItem.processing.personal_information_detection?.detected ? 'text-red-600' : 'text-gray-400'}`} />
                  <span className="font-semibold text-sm">Privacy Scan</span>
                </div>
                {activeItem.processing.personal_information_detection?.detected && (
                  <div className="text-sm text-red-800">
                    Found sensitive data ({activeItem.processing.personal_information_detection.confidence} confidence).
                  </div>
                )}
              </div>

              {/* Taxonomy Card */}
              <div className={`p-4 rounded-xl border transition-all duration-500 bg-gray-50 border-gray-100`}>
                <div className="flex items-center gap-3 mb-2">
                  <FolderTree className="w-5 h-5 text-gray-500" />
                  <span className="font-semibold text-sm">Classification</span>
                </div>
                {activeItem.processing.suggested_function_classification_id && activeItem.processing.potential_function_classifications?.[0] && (
                  <div className="text-sm text-gray-800 font-medium truncate">
                    {activeItem.processing.potential_function_classifications[0].subdomain.subdomain_name}
                    {activeItem.processing.potential_function_classifications[0].function.function_name && (
                      <span className="text-gray-500"> &rsaquo; {activeItem.processing.potential_function_classifications[0].function.function_name}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderErrorState = () => {
    if (!activeItem) return null;
    return (
      <div className="max-w-2xl mx-auto mt-12 animate-in fade-in zoom-in-95 duration-300">
        <div className="bg-white rounded-2xl shadow-xl border border-red-100 overflow-hidden text-center p-12">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-red-600 mx-auto mb-6">
            <AlertTriangle className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Failed</h2>
          <p className="text-gray-500 mb-6">
            We encountered an issue while analyzing <span className="font-medium text-gray-900">{activeItem.file.name}</span>.
          </p>

          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm text-left mb-8 overflow-x-auto max-h-64 overflow-y-auto">
            <p className="text-red-400 mb-2">// Status</p>
            <p className="mb-4">{activeItem.status}</p>
            {Array.isArray(activeItem.errors) && activeItem.errors.length > 0 && (
              <>
                <p className="text-red-400 mb-2">// Errors</p>
                <ul className="list-none space-y-2 m-0 p-0">
                  {activeItem.errors.map((err, i) => {
                    const line = formatIngestionErrorSummary(err);
                    if (!line) return null;
                    return (
                      <li key={i} className="text-gray-200 whitespace-pre-wrap break-words">
                        {err.code && <span className="text-amber-300/90">{err.code}: </span>}
                        {err.message || line}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>

          <div className="flex justify-center gap-4">
            <button onClick={handleReset} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleRetry} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Retry Upload
            </button>
          </div>
        </div>
      </div>
    )
  };

  const renderPotentialVersions = (isConfirmed: boolean) => {
    if (!activeItem || !activeItem.processing.potential_previous_versions || activeItem.processing.potential_previous_versions.length === 0) return null;

    const versions = activeItem.processing.potential_previous_versions;
    const selectedId = activeItem.processing.suggested_previous_version_id;
    const primary = versions.filter(
      (version) => version.confidence === 'high' || version.record_id === selectedId
    );
    const secondary = versions.filter(
      (version) => version.confidence !== 'high' && version.record_id !== selectedId
    );

    const renderVersionRow = (version: (typeof versions)[number]) => {
      const isLinked = selectedId === version.record_id;

      return (
        <div
          key={version.record_id}
          className={`flex items-center justify-between p-3 rounded-xl border transition-all ${isLinked
            ? 'bg-orange-50 border-orange-200 shadow-sm'
            : `bg-white border-gray-100 ${!isConfirmed ? 'hover:border-orange-200 hover:bg-orange-50/30' : ''}`
            }`}
        >
          <div className="min-w-0">
            <a
              href={`/records/${version.record_id}`}
              target="_blank"
              rel="noopener noreferrer"
              title={`${version.title || version.name}`}
              className="group/open flex items-center min-w-0 text-sm font-medium text-gray-900 hover:underline"
            >
              <span className="truncate">{version.title || version.name}</span>
              <ExternalLink className={`w-3.5 h-3.5 ml-1.5 shrink-0 ${BRAND_TEXT}`} />
            </a>
            {version.confidence && (
              <div className="mt-1">
                <ConfidencePill score={version.confidence} />
              </div>
            )}
          </div>

          {isLinked ? (
            <button
              type="button"
              onClick={handleClearPreviousVersionLink}
              disabled={isConfirmed}
              className={`flex items-center shrink-0 ml-3 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors border ${isConfirmed ? 'text-orange-600 bg-orange-100 border-orange-200 opacity-50 cursor-not-allowed' : 'text-orange-600 bg-orange-100 border-orange-200 hover:bg-orange-200 hover:border-orange-300'}`}
            >
              <Link className="w-3 h-3 mr-1" /> Unlink
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleMarkAsPreviousVersion(version)}
              disabled={isConfirmed}
              className={`shrink-0 ml-3 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors border ${isConfirmed ? 'text-gray-400 border-gray-200 opacity-50 cursor-not-allowed' : 'text-gray-500 hover:text-orange-600 hover:bg-orange-100 border-gray-200 hover:border-orange-200'}`}
            >
              Link
            </button>
          )}
        </div>
      );
    };

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-orange-100 p-6 mb-6 animate-in fade-in slide-in-from-top-4 duration-500 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-5">
          <GitCommit className="w-32 h-32 text-orange-500" />
        </div>

        <div className="relative z-10">
          <h3 className="text-sm font-bold text-orange-600 uppercase tracking-wider mb-2 flex items-center">
            <GitCommit className="w-4 h-4 mr-2" /> Potential Previous Versions
          </h3>
          <p className="text-sm text-gray-600 mb-4 max-w-2xl">
            We found similar documents that might be previous versions of this file.
            High-confidence matches are shown first and selected when we are confident.
            Link one to keep a complete document history, or leave them unlinked if this is a new document.
          </p>

          {primary.length > 0 && (
            <div className="space-y-3">
              {primary.map(renderVersionRow)}
            </div>
          )}

          {secondary.length > 0 && (
            <>
              <button
                type="button"
                aria-expanded={showWeakerVersionMatches}
                onClick={() => setShowWeakerVersionMatches((open) => !open)}
                className={`flex items-center text-sm text-gray-500 hover:text-gray-700 ${primary.length > 0 ? 'mt-3' : ''}`}
              >
                {showWeakerVersionMatches ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                {secondary.length} lower-confidence match{secondary.length === 1 ? '' : 'es'}
              </button>
              {showWeakerVersionMatches && (
                <div className="space-y-3 mt-3">
                  {secondary.map(renderVersionRow)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderDuplicateWarning = () => {
    if (!activeItem || activeItem.status !== 'discarded' || !activeItem.processing.duplicate) return null;

    return (
      <div className="bg-red-50 rounded-2xl shadow-sm border border-red-200 p-6 mb-6 animate-in fade-in slide-in-from-top-4 duration-500 flex items-start space-x-4">
        <div className="p-3 bg-red-100 rounded-full shrink-0">
          <AlertTriangle className="w-6 h-6 text-red-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-red-800 tracking-tight">Duplicate File Detected</h3>
          <p className="text-sm text-red-600 mt-1 max-w-2xl">
            This file has been discarded because an exact duplicate already exists in the system.
          </p>
          {activeItem.record_id && (
            <a
              href={`/records/${activeItem.record_id}`}
              onClick={(event) => {
                if (!event.ctrlKey && !event.metaKey) {
                  event.preventDefault();
                  navigate(`/records/${activeItem.record_id}`);
                }
              }}
              className={`mt-3 inline-block text-sm font-medium ${BRAND_TEXT} hover:underline`}
            >
              View existing record
            </a>
          )}
        </div>
      </div>
    );
  };

  const renderIngestionFailureDetails = () => {
    if (!activeItem || activeItem.status !== 'failed') return null;

    const entries = Array.isArray(activeItem.errors) ? activeItem.errors : [];
    const failedAt = activeItem.failed_at
      ? (activeItem.failed_at instanceof Date
        ? activeItem.failed_at
        : new Date(activeItem.failed_at))
      : null;

    return (
      <div className="bg-red-50 rounded-2xl shadow-sm border border-red-200 p-6 mb-6 animate-in fade-in slide-in-from-top-4 duration-500 flex items-start space-x-4">
        <div className="p-3 bg-red-100 rounded-full shrink-0">
          <AlertTriangle className="w-6 h-6 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-red-800 tracking-tight">Processing failed</h3>
          {failedAt && !Number.isNaN(failedAt.getTime()) && (
            <p className="text-xs text-red-700/80 mt-1">
              Failed at {failedAt.toLocaleString()}
            </p>
          )}
          {entries.length > 0 ? (
            <ul className="mt-3 space-y-3 list-none p-0 m-0">
              {entries.map((err, i) => {
                const summary = formatIngestionErrorSummary(err);
                if (!summary) return null;
                return (
                  <li
                    key={i}
                    className="text-sm text-red-800 bg-white/60 border border-red-100 rounded-lg p-3 break-words"
                  >
                    {err.code && (
                      <span className="block text-xs font-mono text-red-900/90 mb-1">{err.code}</span>
                    )}
                    {err.message ? (
                      <span className="text-red-900 whitespace-pre-wrap">{err.message}</span>
                    ) : !err.code ? (
                      <span className="text-red-900 whitespace-pre-wrap">{summary}</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-red-700 mt-2 max-w-2xl">
              No detailed error message was stored for this run. Check backend logs if the problem continues.
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderErrorBanner = () => {
    if (!errorMessage) return null;

    return (
      <div className="bg-red-50 rounded-2xl shadow-sm border border-red-200 p-6 mb-6 animate-in fade-in slide-in-from-top-4 duration-500 flex items-start space-x-4">
        <div className="p-3 bg-red-100 rounded-full shrink-0">
          <AlertTriangle className="w-6 h-6 text-red-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-red-800 tracking-tight">Error</h3>
          <p className="text-sm text-red-600 mt-1 max-w-2xl">{errorMessage}</p>
        </div>
        <button onClick={() => setErrorMessage(null)} className="text-red-400 hover:text-red-600 transition-colors shrink-0">
          <XCircle className="w-5 h-5" />
        </button>
      </div>
    );
  };

  const formatStatus = (status: string) => {
    if (!status) return '';
    if (status.toLowerCase() === 'completed') return 'Ready for Review';
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const ProcessingSteps = ({ steps }: { steps?: any }) => {
    if (!steps || !steps.all_steps || steps.all_steps.length === 0) return null;

    const formatStepName = (name: string) => {
      if (!name) return '';
      const withSpaces = name.replace(/_/g, ' ');
      return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    };



    return (
      <div className="bg-white/10 rounded-lg p-3 text-sm border border-white/20">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => setStepsExpanded(!stepsExpanded)}>
          <div className="flex items-center space-x-2">
            <span className="font-semibold text-emerald-300">Current Step:</span>
            <span>{formatStepName(steps.current_step)}</span>
          </div>
          <button className="text-gray-400 hover:text-white">
            {stepsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {stepsExpanded && (
          <div className="mt-3 space-y-2 border-t border-white/10 pt-2 pb-1 relative">
            {steps.all_steps.map((step: any, idx: number) => {
              const isLast = idx === steps.all_steps.length - 1;
              const nextStep = !isLast ? steps.all_steps[idx + 1] : null;

              return (
                <div key={idx} className="flex relative items-start pb-2">
                  <div className="absolute left-2 top-4 bottom-0 w-0.5 bg-white/20"></div>
                  <div className={`relative z-10 w-4 h-4 mt-0.5 rounded-full flex items-center justify-center shrink-0 border-2 ${!isLast ? 'bg-emerald-500 border-emerald-500' : 'bg-transparent border-emerald-400'}`}>
                    {!isLast && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                    {isLast && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>}
                  </div>
                  <div className="ml-3 flex-1 flex justify-between">
                    <span className={!isLast ? 'text-gray-300' : 'text-white font-medium'}>{formatStepName(step.step)}</span>
                    <span className="text-xs text-gray-400">
                      {step.started_at && <StepDuration
                        startedAt={step.started_at}
                        nextStepStartedAt={nextStep?.started_at}
                        isLast={isLast}
                        processStatus={activeItem?.status}
                        completedAt={activeItem?.completed_at}
                        failedAt={activeItem?.failed_at}
                        processModifiedAt={activeItem?.metadata?.modified}
                      />}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderReviewState = () => {
    if (!activeItem) return null;
    const isReadyToFile = activeItem.status === 'completed';
    const isConfirmed = activeItem.status === 'confirmed' || activeItem.status === 'discarded';
    const titleError = confirmFieldErrors.suggested_title;
    const summaryError = confirmFieldErrors.content_summary;
    const recordClassificationError = confirmFieldErrors.suggested_record_classification_id;
    const functionClassificationError = confirmFieldErrors.suggested_function_classification_id;

    return (
      <div className="max-w-5xl mx-auto mt-8 pb-20">

        {/* Back navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleReset}
            className="flex items-center text-sm text-gray-500 hover:text-emerald-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back
          </button>
          {uploadFeedbackFields.length > 0 && !['initiated', 'in_progress'].includes(activeItem.status) && (
            <FeedbackThumbs
              key={activeItem.id}
              surface="upload"
              targetId={activeItem.id}
              fields={uploadFeedbackFields}
              prompt="AI suggestions helpful?"
              placement="bottom"
              align="right"
            />
          )}
        </div>

        {renderDuplicateWarning()}
        {renderErrorBanner()}
        {renderIngestionFailureDetails()}

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden relative">
          {/* Top Header / Context */}
          <div className="bg-gray-900 text-white p-6 flex flex-col md:flex-row md:items-start justify-between gap-6 rounded-t-3xl">

            <div className="flex items-start space-x-4 flex-1">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shrink-0">
                {getFileIcon(activeItem.file.content_type)}
              </div>
              <div className="flex-1 w-full min-w-0">
                <div className="flex justify-between items-start w-full gap-4">
                  <h1 className="text-xl font-bold tracking-tight break-words pr-4">{activeItem.file.name}</h1>
                </div>

                <div className="flex flex-wrap items-center mt-2 gap-3">
                  <div className={`px-3 py-1 rounded-full text-xs font-bold tracking-wider flex items-center shadow-lg
                    ${activeItem.status === 'completed' ? 'bg-emerald-500 text-white' :
                      activeItem.status === 'failed' ? 'bg-red-500 text-white' :
                        activeItem.status === 'in_progress' ? 'bg-amber-500 text-white' :
                          'bg-white/20 text-white backdrop-blur-sm border border-white/20'}`}>
                    {activeItem.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1.5" />}
                    {activeItem.status === 'in_progress' && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
                    {formatStatus(activeItem.status)}
                  </div>

                  <span className="text-gray-400 text-sm flex items-center">
                    <Clock className="w-3.5 h-3.5 mr-1" />
                    <StepDuration
                      startedAt={activeItem.initiated_at}
                      isLast={true}
                      processStatus={activeItem.status}
                      completedAt={activeItem.completed_at}
                      failedAt={activeItem.failed_at}
                      processModifiedAt={activeItem.metadata.modified}
                    />
                  </span>
                </div>

                <div className="mt-4 max-w-2xl">
                  {activeItem.status === 'in_progress' && (
                    <ProcessingSteps steps={activeItem.steps} />
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[600px]">
            {/* Left Column (1/3 Width) */}
            <div className="lg:col-span-4 bg-gray-50 border-r border-gray-100 p-6 space-y-6 flex flex-col">

              <ThumbnailPreview
                thumbnailHref={
                  activeItem.processing?.thumbnail
                    ? `/api/v1/processing/${activeItem.id}/thumbnail`
                    : null
                }
              />

              {/* Suggested Address */}
              <GeographicalContextEditable
                tooltipContent="Identifies address mentioned in the document and validates it against GIS."
                suggestedAddress={activeItem.processing.geographical_context?.suggested_address || ''}
                onAddressChange={(val) => handleUpdateField('processing.geographical_context.suggested_address', val)}
                onValidate={async () => {
                  const address = activeItem.processing.geographical_context?.suggested_address;
                  if (!address || !activeItem.id || isConfirmed || isValidatingAddress) return;
                  setIsValidatingAddress(true);
                  try {
                    const resp = await apiFetch(`/api/v1/processing/${activeItem.id}/address`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ suggested_address: address }),
                    });
                    if (!resp.ok) {
                      const msg = await extractErrorMessage(resp, `Address validation failed (${resp.status})`);
                      throw new Error(msg);
                    }
                  } catch (err: any) {
                    console.error(err);
                    addToast(err?.message || 'Failed to validate address.', 'info');
                  } finally {
                    setIsValidatingAddress(false);
                  }
                }}
                isValidating={isValidatingAddress}
                readOnly={isConfirmed}
              >
                <GeographicalContextAddressesProcessing
                  parsed_street={activeItem.processing.geographical_context?.parsed_street}
                  validated_street={activeItem.processing.geographical_context?.validated_street}
                  parsed_addresses={activeItem.processing.geographical_context?.parsed_addresses}
                  validated_addresses={activeItem.processing.geographical_context?.validated_addresses}
                />
                <GeographicalContextValuationsParcels
                  rating_valuations={activeItem.processing.geographical_context?.rating_valuations}
                  parcels={activeItem.processing.geographical_context?.parcels}
                />
              </GeographicalContextEditable>

              {/* File Details */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-4 bg-gray-50/50 border-b border-gray-100 rounded-t-xl">
                  <h3 className="text-sm font-bold text-gray-900">File Details</h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Type</span>
                    <span className="font-semibold text-gray-900">{getFileTypeLabel(activeItem.file.content_type)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-t border-gray-50 pt-2">
                    <span className="text-gray-500">Size</span>
                    <span className="font-semibold text-gray-900">{formatBytes(activeItem.file.size)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-t border-gray-50 pt-2">
                    <span className="text-gray-500">Uploaded</span>
                    <span className="font-semibold text-gray-900">
                      {activeItem.initiated_at ? activeItem.initiated_at.toLocaleString() : 'Processing...'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-t border-gray-50 pt-2">
                    <span className="text-gray-500 shrink-0 mr-2 flex items-center">
                      File Integrity
                      <Tooltip content="Digital fingerprint ensuring the file cannot be altered."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                    </span>
                    <div className={`flex items-center px-2 py-1 rounded-md ${activeItem.file.checksum === 'Pending' ? 'bg-gray-100 text-gray-500' : 'bg-emerald-50 text-emerald-600'}`}>
                      <ShieldCheck className="w-4 h-4 mr-1.5" />
                      <span className="font-semibold text-xs">{activeItem.file.checksum === 'Pending' ? 'Pending' : 'Secured & Verified'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Immutability / Final Record */}
              <div className="bg-emerald-50/30 rounded-xl border border-emerald-100 relative group transition-all hover:bg-emerald-50">
                <div className="p-4 flex justify-between items-start rounded-t-xl">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 flex items-center">
                      Mark as Final Record
                      <Tooltip content="Final records are locked and immutable."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                    </h3>
                  </div>
                  <ToggleSwitch
                    enabled={activeItem.processing.immutability_detection?.final || false}
                    onChange={(val) => {
                      handleUpdateField('processing.immutability_detection.final', val);
                      const originalVal = ingestionProcess?.processing?.immutability_detection?.final || false;
                      handleUpdateField('processing.immutability_detection.overridden', val !== originalVal);
                    }}
                    disabled={isConfirmed}
                  />
                </div>
                <div className="px-4 pb-4">
                  {activeItem.processing.immutability_detection?.overridden ? (
                    <div className="flex items-center text-sm font-medium text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-100">
                      <span className="text-xs font-bold text-gray-500 bg-gray-200 px-2 py-0.5 rounded-md mr-2">OVERRIDDEN</span>
                      Manually set by user
                    </div>
                  ) : (
                    <details className="group/reasoning">
                      <summary className="flex items-center justify-between cursor-pointer list-none text-xs text-emerald-600 font-semibold hover:text-emerald-700 transition-colors">
                        <span className="flex items-center">
                          <ChevronRight className="w-3.5 h-3.5 mr-1 transition-transform group-open/reasoning:rotate-90" />
                          View AI Reasoning
                        </span>
                        <ConfidencePill score={activeItem.processing.immutability_detection?.confidence || 'low'} />
                      </summary>
                      <div className={`mt-3 text-xs p-3 rounded border shadow-sm animate-in slide-in-from-top-1 fade-in duration-200 ${activeItem.processing.immutability_detection?.final ? 'text-emerald-800 bg-emerald-50/30 border-emerald-100' : 'text-gray-600 bg-white border-gray-100'}`}>
                        <p>{activeItem.processing.immutability_detection?.reason}</p>
                      </div>
                    </details>
                  )}
                </div>
              </div>

            </div>

            {/* Main Content: Form */}
            {/* Right Column (2/3 Width) */}
            <div className="lg:col-span-8 p-6 md:p-8 space-y-6">

              {/* Potential Previous Versions */}
              {renderPotentialVersions(isConfirmed)}

              {/* Record title */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between rounded-t-xl">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-sm font-bold text-gray-900 flex items-center">
                      Record Title
                      <Tooltip content="Suggested by AI after processing, or from upload metadata. You can edit before confirming."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                    </h3>
                  </div>
                </div>
                <div className="p-4">
                  <input
                    id="confirm-field-suggested_title"
                    type="text"
                    className={`w-full p-3 rounded-lg border bg-white text-sm text-gray-700 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-inner ${titleError ? 'border-red-400' : 'border-gray-200'}`}
                    value={activeItem.processing.suggested_title ?? ''}
                    onChange={(e) => handleUpdateField('processing.suggested_title', e.target.value)}
                    placeholder="Set after processing completes, or enter before confirming"
                    readOnly={isConfirmed}
                    aria-invalid={titleError ? true : undefined}
                  />
                  {titleError && <p className="mt-2 text-xs text-red-600">{titleError}</p>}
                </div>
              </div>

              {/* Summary Section */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between rounded-t-xl">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-sm font-bold text-gray-900 flex items-center">
                      AI Summary
                      <Tooltip content="A brief, AI-generated overview of the document's main points."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                    </h3>
                  </div>
                </div>
                <div className="p-4">
                  <textarea
                    id="confirm-field-content_summary"
                    className={`w-full h-40 p-3 rounded-lg border bg-white text-sm text-gray-700 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none shadow-inner ${summaryError ? 'border-red-400' : 'border-gray-200'}`}
                    value={activeItem.processing.content_summary || ''}
                    onChange={(e) => handleUpdateField('processing.content_summary', e.target.value)}
                    placeholder="Provide a summary of the document's contents..."
                    readOnly={isConfirmed}
                    aria-invalid={summaryError ? true : undefined}
                  />
                  {summaryError && <p className="mt-2 text-xs text-red-600">{summaryError}</p>}
                </div>
              </div>

              {/* Classification Container */}
              <div className="space-y-6">

                {/* Record Classification */}
                <div id="confirm-field-suggested_record_classification_id" className={`bg-white border rounded-xl shadow-sm relative group ${recordClassificationError ? 'border-red-400' : 'border-gray-200'}`}>
                  <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center rounded-t-xl">
                    <label className="text-sm font-bold text-gray-900 flex items-center">
                      <FolderTree className="w-4 h-4 text-emerald-600 mr-2" />
                      Record Classification
                      <Tooltip content="The formal categorisation determining the type of record."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                    </label>
                  </div>
                  {recordClassificationError && (
                    <p className="px-4 pt-3 text-xs text-red-600">{recordClassificationError}</p>
                  )}
                  <div className="p-4 space-y-3">
                    {selectedClassification?.level_1?.name ||
                     selectedClassification?.level_2?.name ||
                     selectedClassification?.level_3?.name ? (
                      <div className="space-y-3">
                        <div>
                          <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Function</div>
                          <div
                            className={`text-sm font-semibold transition-colors truncate ${!isConfirmed ? 'text-gray-900 hover:text-emerald-600 cursor-pointer' : 'text-gray-900'}`}
                            title={selectedClassification?.level_1?.name || ''}
                            onClick={() => !isConfirmed && openClassificationPicker(1)}
                          >
                            {selectedClassification?.level_1?.name || <span className="italic text-gray-400">Not set</span>}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Activity</div>
                          <div
                            className={`text-sm font-semibold transition-colors truncate ${!isConfirmed ? 'text-emerald-800 hover:text-emerald-600 cursor-pointer' : 'text-emerald-800'}`}
                            title={selectedClassification?.level_2?.name || ''}
                            onClick={() => !isConfirmed && openClassificationPicker(2)}
                          >
                            {selectedClassification?.level_2?.name || <span className="italic text-gray-400">Not set</span>}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Record Type</div>
                          <div
                            className={`text-sm font-bold break-words leading-tight transition-colors ${!isConfirmed ? 'text-gray-900 hover:text-emerald-600 cursor-pointer' : 'text-gray-900'}`}
                            onClick={() => !isConfirmed && openClassificationPicker(3)}
                          >
                            {selectedClassification?.level_3?.name || <span className="italic text-gray-400">Not set</span>}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`text-sm font-medium italic transition-colors ${!isConfirmed ? 'text-gray-500 hover:text-emerald-600 cursor-pointer' : 'text-gray-500'}`}
                        onClick={() => !isConfirmed && openClassificationPicker(1)}
                      >
                        Classification Pending or Unknown
                      </div>
                    )}

                    <details className="group/details pb-1">
                      <summary className="text-xs text-emerald-600 font-semibold cursor-pointer select-none hover:text-emerald-700 list-none flex items-center">
                        <ChevronRight className="w-3 h-3 mr-1 transition-transform group-open/details:rotate-90" />
                        Show Details
                      </summary>
                      <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-100 italic">
                        {selectedClassification?.level_3?.description || 'No description available for this classification.'}
                      </div>
                    </details>

                    {activeItem.processing.potential_record_classifications && activeItem.processing.potential_record_classifications.length > 0 && (
                      <details className="group/potential-classifications pb-1">
                        <summary className="text-xs text-emerald-600 font-semibold cursor-pointer select-none hover:text-emerald-700 list-none flex items-center">
                          <ChevronRight className="w-3 h-3 mr-1 transition-transform group-open/potential-classifications:rotate-90" />
                          AI Suggestions ({activeItem.processing.potential_record_classifications.length})
                        </summary>
                        <div className="mt-2 space-y-1.5">
                          {activeItem.processing.potential_record_classifications.map((classification, idx) => {
                            const isSelected = !manualClassification && activeItem.processing.suggested_record_classification_id === classification.classification_id;

                            return (
                              <button
                                key={classification.classification_id}
                                disabled={isConfirmed}
                                onClick={() => applyClassificationById(classification.classification_id)}
                                className={`w-full text-left p-2.5 rounded-lg border transition-all text-xs ${
                                  isSelected
                                    ? 'bg-emerald-50 border-emerald-200 shadow-sm'
                                    : isConfirmed
                                      ? 'bg-white border-gray-100 opacity-50 cursor-not-allowed'
                                      : 'bg-white border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/30'
                                }`}
                              >
                                <div className="flex items-start gap-2.5">
                                  <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                    isSelected ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {idx + 1}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-gray-400 font-normal truncate" title={classification.level_1.name}>{classification.level_1.name}</div>
                                    <div className="text-gray-400 font-normal truncate" title={classification.level_2.name}>{classification.level_2.name}</div>
                                    <div className={`font-medium leading-tight truncate ${isSelected ? 'text-emerald-800' : 'text-gray-900'}`} title={classification.level_3.name}>{classification.level_3.name}</div>
                                  </div>
                                  {isSelected && <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </details>
                    )}
                  </div>
                </div>

                {/* Function Classification */}
                <div id="confirm-field-suggested_function_classification_id" className={`bg-white border rounded-xl shadow-sm relative group ${functionClassificationError ? 'border-red-400' : 'border-gray-200'}`}>
                  <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center rounded-t-xl">
                    <label className="text-sm font-bold text-gray-900 flex items-center">
                      <Database className="w-4 h-4 text-emerald-600 mr-2" />
                      Business Function
                      <Tooltip content="The primary business activity or purpose this document relates to."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                    </label>
                  </div>
                  {functionClassificationError && (
                    <p className="px-4 pt-3 text-xs text-red-600">{functionClassificationError}</p>
                  )}
                  <div className="p-4 space-y-3">
                    {selectedFunctionClassification ? (
                      <div className="space-y-3">
                        <div>
                          <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Domain</div>
                          <div
                            className={`text-sm font-semibold transition-colors truncate ${!isConfirmed ? 'text-gray-900 hover:text-emerald-600 cursor-pointer' : 'text-gray-900'}`}
                            title={selectedFunctionClassification.domain.domain_name}
                            onClick={() => !isConfirmed && openFunctionPicker()}
                          >
                            {selectedFunctionClassification.domain.domain_name}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Subdomain</div>
                          <div
                            className={`text-sm font-semibold transition-colors truncate ${!isConfirmed ? 'text-gray-900 hover:text-emerald-600 cursor-pointer' : 'text-gray-900'}`}
                            title={selectedFunctionClassification.subdomain.subdomain_name}
                            onClick={() => !isConfirmed && openFunctionPickerAtSubdomain()}
                          >
                            {selectedFunctionClassification.subdomain.subdomain_name}
                          </div>
                        </div>
                        {selectedFunctionClassification.function.function_name && (
                          <div>
                            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">Function</div>
                            <div
                              className={`text-sm font-semibold transition-colors truncate ${!isConfirmed ? 'text-emerald-800 hover:text-emerald-600 cursor-pointer' : 'text-emerald-800'}`}
                              title={selectedFunctionClassification.function.function_name}
                              onClick={() => !isConfirmed && openFunctionPickerAtFunction()}
                            >
                              {selectedFunctionClassification.function.function_name}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`text-sm font-medium italic transition-colors ${!isConfirmed ? 'text-gray-500 hover:text-emerald-600 cursor-pointer' : 'text-gray-500'}`}
                        onClick={() => !isConfirmed && openFunctionPicker()}
                      >
                        Classification Pending or Unknown
                      </div>
                    )}

                    {activeItem.processing.potential_function_classifications && activeItem.processing.potential_function_classifications.length > 0 && (
                      <details className="group/potential-functions pb-1">
                        <summary className="text-xs text-emerald-600 font-semibold cursor-pointer select-none hover:text-emerald-700 list-none flex items-center">
                          <ChevronRight className="w-3 h-3 mr-1 transition-transform group-open/potential-functions:rotate-90" />
                          AI Suggestions ({activeItem.processing.potential_function_classifications.length})
                        </summary>
                        <div className="mt-2 space-y-1.5">
                          {activeItem.processing.potential_function_classifications.map((classification, idx) => {
                            const isSelected = !manualFunctionClassification && activeItem.processing.suggested_function_classification_id === classification.classification_id;

                            return (
                              <button
                                key={classification.classification_id}
                                disabled={isConfirmed}
                                onClick={() => applyFunctionClassificationById(classification.classification_id)}
                                className={`w-full text-left p-2.5 rounded-lg border transition-all text-xs ${
                                  isSelected
                                    ? 'bg-emerald-50 border-emerald-200 shadow-sm'
                                    : isConfirmed
                                      ? 'bg-white border-gray-100 opacity-50 cursor-not-allowed'
                                      : 'bg-white border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/30'
                                }`}
                              >
                                <div className="flex items-start gap-2.5">
                                  <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                    isSelected ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {idx + 1}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-gray-400 font-normal truncate" title={classification.domain.domain_name}>{classification.domain.domain_name}</div>
                                    <div className="text-gray-500 font-normal truncate" title={classification.subdomain.subdomain_name}>{classification.subdomain.subdomain_name}</div>
                                    <div className={`font-medium leading-tight truncate ${isSelected ? 'text-emerald-800' : 'text-gray-900'}`} title={classification.function.function_name}>{classification.function.function_name}</div>
                                  </div>
                                  {isSelected && <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </details>
                    )}
                  </div>
                </div>

              </div>

              {/* Governance & Sensitivity */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm relative group overflow-hidden">
                <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center rounded-t-xl">
                  <label className="text-sm font-bold text-gray-900 flex items-center">
                    <ShieldAlert className="w-4 h-4 text-emerald-600 mr-2" />
                    Governance & Sensitivity
                  </label>
                </div>

                <div className="divide-y divide-gray-100">

                  {/* Information Security Classification */}
                  <div className={`p-5 transition-all ${activeItem.processing.information_security_classification_detection?.overridden ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 flex items-center">
                          Information Security Classification
                          <Tooltip content="Determines the information security classification based on file sensitivity."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                        </h4>
                      </div>
                      <div className="flex items-center space-x-4">
                        {!activeItem.processing.information_security_classification_detection?.overridden && <ConfidencePill score={activeItem.processing.information_security_classification_detection?.confidence || 'low'} />}
                        <select
                          className="text-sm font-bold text-gray-900 bg-gray-50 border border-gray-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-lg px-2 py-1 transition-colors"
                          value={activeItem.processing.information_security_classification_detection?.level || 'unclassified'}
                          onChange={(e) => {
                            handleUpdateField('processing.information_security_classification_detection.level', e.target.value);
                            const originalVal = ingestionProcess?.processing?.information_security_classification_detection?.level || 'unclassified';
                            handleUpdateField('processing.information_security_classification_detection.overridden', e.target.value !== originalVal);
                          }}
                          disabled={isConfirmed}
                        >
                          <option value="unclassified">Unclassified</option>
                          <option value="in-confidence">In-Confidence</option>
                          <option value="sensitive">Sensitive</option>
                          <option value="restricted">Restricted</option>
                        </select>
                      </div>
                    </div>

                    {activeItem.processing.information_security_classification_detection?.overridden ? (
                      <div className="flex items-center text-sm font-medium text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-100 mt-3">
                        <span className="text-xs font-bold text-gray-500 bg-gray-200 px-2 py-0.5 rounded-md mr-2">OVERRIDDEN</span>
                        Manually set by user
                      </div>
                    ) : (activeItem.processing.information_security_classification_detection?.reason) ? (
                      <details className="group/reasoning mt-3">
                        <summary className="flex items-center cursor-pointer list-none text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors">
                          <ChevronRight className="w-3.5 h-3.5 mr-1 transition-transform group-open/reasoning:rotate-90" />
                          View AI Reasoning
                        </summary>
                        <div className="mt-3 text-xs p-3 rounded border shadow-sm animate-in slide-in-from-top-1 fade-in duration-200 text-gray-600 bg-gray-50 border-gray-100">
                          <p>{activeItem.processing.information_security_classification_detection?.reason}</p>
                        </div>
                      </details>
                    ) : null}
                  </div>

                  {/* Personal Information */}
                  <div className={`p-5 transition-all ${activeItem.processing.personal_information_detection?.overridden ? 'bg-gray-50 opacity-60' : activeItem.processing.personal_information_detection?.detected ? 'bg-red-50/50' : 'bg-white'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 flex items-center">
                          Personal Information
                          <Tooltip content="Detects names, contact info, and sensitive identifiers."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                        </h4>
                      </div>
                      <div className="flex items-center space-x-4">
                        {!activeItem.processing.personal_information_detection?.overridden && <ConfidencePill score={activeItem.processing.personal_information_detection?.confidence || 'low'} />}
                        <div className="flex flex-col items-center">
                          <ToggleSwitch
                            enabled={activeItem.processing.personal_information_detection?.detected || false}
                            onChange={(val) => {
                              const original = ingestionProcess?.processing?.personal_information_detection;
                              const originalDetected = original?.detected || false;
                              const originalLevel = original?.sensitivity_level || null;
                              handleUpdateField('processing.personal_information_detection.detected', val);
                              const nextLevel = val
                                ? (() => {
                                    const current = activeItem.processing?.personal_information_detection?.sensitivity_level;
                                    const normalised = current?.toLowerCase();
                                    if (!normalised || normalised === 'none') return 'standard';
                                    return current;
                                  })()
                                : null;
                              handleUpdateField('processing.personal_information_detection.sensitivity_level', nextLevel);
                              handleUpdateField(
                                'processing.personal_information_detection.overridden',
                                val !== originalDetected || (val && nextLevel !== originalLevel),
                              );
                            }}
                            disabled={isConfirmed}
                          />
                        </div>
                      </div>
                    </div>

                    {activeItem.processing.personal_information_detection?.detected && (() => {
                      const rawLevel = activeItem.processing.personal_information_detection?.sensitivity_level || '';
                      const piiLevel = ['standard', 'sensitive'].includes(rawLevel.toLowerCase())
                        ? rawLevel.toLowerCase()
                        : 'standard';
                      return (
                      <div className="flex items-center gap-2 mt-2 mb-1">
                        <span className="text-xs font-semibold text-gray-600">Sensitivity</span>
                        {!isConfirmed && (
                          <select
                            className="text-sm font-bold text-gray-900 bg-gray-50 border border-gray-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 rounded-lg px-2 py-1 transition-colors"
                            value={piiLevel}
                            onChange={(e) => {
                              const nextLevel = e.target.value;
                              const original = ingestionProcess?.processing?.personal_information_detection;
                              const originalDetected = original?.detected || false;
                              const originalLevel = original?.sensitivity_level || null;
                              handleUpdateField('processing.personal_information_detection.sensitivity_level', nextLevel);
                              handleUpdateField(
                                'processing.personal_information_detection.overridden',
                                true !== originalDetected || nextLevel !== originalLevel,
                              );
                            }}
                            disabled={isConfirmed}
                          >
                            {PII_SENSITIVITY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value} title={opt.description}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      );
                    })()}

                    {activeItem.processing.personal_information_detection?.overridden ? (
                      <div className="flex items-center text-sm font-medium text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-100 mt-3">
                        <span className="text-xs font-bold text-gray-500 bg-gray-200 px-2 py-0.5 rounded-md mr-2">OVERRIDDEN</span>
                        Manually set by user
                      </div>
                    ) : (activeItem.processing.personal_information_detection?.reason) ? (
                      <div className="mt-3">
                        <details className="group/reasoning">
                          <summary className={`flex items-center cursor-pointer list-none text-xs font-semibold transition-colors ${activeItem.processing.personal_information_detection?.detected ? 'text-red-700 hover:text-red-800' : 'text-gray-500 hover:text-gray-700'}`}>
                            <ChevronRight className="w-3.5 h-3.5 mr-1 transition-transform group-open/reasoning:rotate-90" />
                            View AI Reasoning
                          </summary>
                          <div className={`mt-3 text-xs p-3 rounded border shadow-sm animate-in slide-in-from-top-1 fade-in duration-200 ${activeItem.processing.personal_information_detection?.detected ? 'text-red-800 bg-red-50/30 border-red-100' : 'text-gray-600 bg-gray-50 border-gray-100'}`}>
                            <p>{activeItem.processing.personal_information_detection?.reason}</p>
                          </div>
                        </details>

                        {activeItem.processing.personal_information_detection.entity_recognition && activeItem.processing.personal_information_detection.entity_recognition.length > 0 && (
                          <details className="mt-3 group/entities">
                            <summary className="font-semibold cursor-pointer select-none text-red-700 text-xs flex items-center">
                              <ChevronRight className="w-3 h-3 mr-1 transition-transform group-open/entities:rotate-90" />
                              View Extracted Entities
                            </summary>
                            <div className="mt-2 space-y-2 pl-4">
                              {activeItem.processing.personal_information_detection.entity_recognition.map((ent, i) => (
                                <div key={i} className="flex flex-col bg-red-50/50 p-2 rounded">
                                  <div className="flex justify-between items-center mb-1">
                                    <div className="flex items-center">
                                      <span className="font-bold text-sm">{ent.entity}</span>
                                      <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 uppercase tracking-wider">{ent.sensitivity_level}</span>
                                    </div>
                                    <span className="bg-red-200 px-1.5 py-0.5 rounded text-sm text-red-800">{ent.category}</span>
                                  </div>
                                  <span className="italic text-gray-500 text-xs">&ldquo;...{ent.context_snippet}...&rdquo;</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {/* Maori Data  */}
                  <div className={`p-5 transition-all ${activeItem.processing.maori_data_sovereignty_detection?.overridden ? 'bg-gray-50 opacity-60' : activeItem.processing.maori_data_sovereignty_detection?.detected ? 'bg-purple-50/50' : 'bg-white'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 flex items-center">
                          Māori Data
                          <Tooltip content="Identifies content related to Māori data sovereignty, culture, or people."><Info className="w-3.5 h-3.5 text-gray-400 ml-1.5 cursor-help" /></Tooltip>
                          {!activeItem.processing.maori_data_sovereignty_detection?.overridden && activeItem.processing.maori_data_sovereignty_detection?.detected && <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 uppercase tracking-wider">{activeItem.processing.maori_data_sovereignty_detection.category}</span>}
                        </h4>
                      </div>
                      <div className="flex items-center space-x-4">
                        {!activeItem.processing.maori_data_sovereignty_detection?.overridden && <ConfidencePill score={activeItem.processing.maori_data_sovereignty_detection?.confidence || 'low'} />}
                        <ToggleSwitch
                          enabled={activeItem.processing.maori_data_sovereignty_detection?.detected || false}
                          onChange={(val) => {
                            handleUpdateField('processing.maori_data_sovereignty_detection.detected', val);
                            const originalVal = ingestionProcess?.processing?.maori_data_sovereignty_detection?.detected || false;
                            handleUpdateField('processing.maori_data_sovereignty_detection.overridden', val !== originalVal);
                          }}
                          disabled={isConfirmed}
                        />
                      </div>
                    </div>

                    {activeItem.processing.maori_data_sovereignty_detection?.overridden ? (
                      <div className="flex items-center text-sm font-medium text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-100 mt-3">
                        <span className="text-xs font-bold text-gray-500 bg-gray-200 px-2 py-0.5 rounded-md mr-2">OVERRIDDEN</span>
                        Manually set by user
                      </div>
                    ) : (activeItem.processing.maori_data_sovereignty_detection?.reason) ? (
                      <details className="group/reasoning mt-3">
                        <summary className={`flex items-center cursor-pointer list-none text-xs font-semibold transition-colors ${activeItem.processing.maori_data_sovereignty_detection?.detected ? 'text-purple-700 hover:text-purple-800' : 'text-gray-500 hover:text-gray-700'}`}>
                          <ChevronRight className="w-3.5 h-3.5 mr-1 transition-transform group-open/reasoning:rotate-90" />
                          View AI Reasoning
                        </summary>
                        <div className={`mt-3 text-xs p-3 rounded border shadow-sm animate-in slide-in-from-top-1 fade-in duration-200 ${activeItem.processing.maori_data_sovereignty_detection?.detected ? 'text-purple-800 bg-purple-50/30 border-purple-100' : 'text-gray-600 bg-gray-50 border-gray-100'}`}>
                          <p>{activeItem.processing.maori_data_sovereignty_detection?.reason}</p>
                        </div>
                      </details>
                    ) : null}
                  </div>

                  {/* Retention Policy */}
                  {SHOW_RETENTION_AND_DISPOSAL && (
                  <div className="p-5 bg-gray-50 flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-b-xl">
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 flex items-center">
                        Retention Policy
                        <Tooltip content="Automatically determined based on Record Classification, cannot be manually overridden."><Info className="w-3.5 h-3.5 ml-1.5 text-gray-400" /></Tooltip>
                      </h4>
                      <div className="text-xs text-gray-500 mt-1">Trigger: <span className="font-medium text-gray-700">{selectedClassification?.retention_period_start_condition || 'N/A'}</span></div>
                    </div>
                    <div className="bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm min-w-[260px]">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-emerald-600 mr-3 shrink-0" />
                        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500 mr-2">Min. Retention Period:</span>
                        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{formatRetentionPeriod(selectedClassification?.minimum_retention_period)}</span>
                      </div>
                      <div className="flex items-center mt-1 ml-7">
                        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Action: {selectedClassification?.disposal_recommendation || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                  )}

                </div>
              </div>

            </div>
          </div>

          {/* Actions Bar */}
          <div className="bg-gray-50 border-t border-gray-200 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

              <div className="flex-1 md:mr-6">
                {['initiated', 'in_progress', 'completed'].includes(activeItem.status) && (
                  <div className="flex items-center text-sm text-gray-600 bg-blue-50/50 text-blue-800 px-4 py-3 rounded-xl border border-blue-100">
                    <Info className="w-5 h-5 mr-3 shrink-0 text-blue-500" />
                    <p>Review all metadata carefully. Overrides are logged for audit purposes.</p>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-4 shrink-0 justify-end w-full md:w-auto">
                <button
                  onClick={handleDiscard}
                  disabled={!isReadyToFile || isSubmitting}
                  className="px-6 py-2.5 rounded-xl text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Discard Record
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!isReadyToFile || isSubmitting}
                  className={`px-8 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg shadow-emerald-200 flex items-center transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none ${BRAND_BG}`}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  {isSubmitting ? 'Processing...' : 'Confirm & File Record'}
                </button>
              </div>
            </div>
          </div>

        </div>

        {showUnlinkedPreviousVersionConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => !isSubmitting && setShowUnlinkedPreviousVersionConfirm(false)}
          >
            <div
              ref={unlinkedPreviousVersionDialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="unlinked-previous-version-title"
              tabIndex={-1}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 outline-none"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-amber-100 rounded-full shrink-0">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 id="unlinked-previous-version-title" className="text-lg font-bold text-gray-900">
                      Previous version not linked
                    </h3>
                    <p className="text-sm text-gray-600 mt-2">
                      A high-confidence previous version was found but none is linked.
                      Are you sure this is a new document?
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowUnlinkedPreviousVersionConfirm(false)}
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Go back
                </button>
                <button
                  type="button"
                  onClick={handleConfirmWithoutPreviousVersion}
                  disabled={isSubmitting}
                  className={`px-4 py-2 rounded-xl text-sm font-bold text-white flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_BG}`}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  {isSubmitting ? 'Processing...' : 'File as new document'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showTaxonomyPicker && (() => {
          const levelLabels = ['Level 1 — Function', 'Level 2 — Activity', 'Level 3 — Record Type'];
          const breadcrumbs = [
            ...(selectedLevel1 ? [{ label: selectedLevel1, level: 1 as const }] : []),
            ...(selectedLevel2 ? [{ label: selectedLevel2, level: 2 as const }] : []),
          ];

          let options: { key: string; label: string; sublabel?: string; onClick: () => void }[] = [];
          const searchLower = classificationSearch.toLowerCase();

          if (pickerLevel === 1) {
            options = Object.keys(classificationHierarchy)
              .sort()
              .filter(l1 => !searchLower || l1.toLowerCase().includes(searchLower))
              .map(l1 => ({
                key: l1,
                label: l1,
                sublabel: `${Object.keys(classificationHierarchy[l1]).length} activities`,
                onClick: () => { setSelectedLevel1(l1); setSelectedLevel2(null); setPickerLevel(2); setClassificationSearch(''); },
              }));
          } else if (pickerLevel === 2 && selectedLevel1 && classificationHierarchy[selectedLevel1]) {
            options = Object.keys(classificationHierarchy[selectedLevel1])
              .sort()
              .filter(l2 => !searchLower || l2.toLowerCase().includes(searchLower))
              .map(l2 => ({
                key: l2,
                label: l2,
                sublabel: `${classificationHierarchy[selectedLevel1][l2].length} record types`,
                onClick: () => { setSelectedLevel2(l2); setPickerLevel(3); setClassificationSearch(''); },
              }));
          } else if (pickerLevel === 3 && selectedLevel1 && selectedLevel2 && classificationHierarchy[selectedLevel1]?.[selectedLevel2]) {
            options = classificationHierarchy[selectedLevel1][selectedLevel2]
              .sort((a, b) => a.level_3.name.localeCompare(b.level_3.name))
              .filter(entry => !searchLower || entry.level_3.name.toLowerCase().includes(searchLower) || entry.level_3.description.toLowerCase().includes(searchLower))
              .map(entry => ({
                key: `${entry.level_1.name}|${entry.level_2.name}|${entry.level_3.name}`,
                label: entry.level_3.name,
                sublabel: entry.level_3.description.length > 120 ? entry.level_3.description.slice(0, 120) + '…' : entry.level_3.description,
                onClick: () => applyClassificationFromTaxonomy(entry),
              }));
          }

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowTaxonomyPicker(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-center shrink-0">
                  <div className="flex items-center gap-3">
                    {pickerLevel > 1 && (
                      <button
                        onClick={() => {
                          setClassificationSearch('');
                          if (pickerLevel === 3) { setSelectedLevel2(null); setPickerLevel(2); }
                          else if (pickerLevel === 2) { setSelectedLevel1(null); setPickerLevel(1); }
                        }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <ArrowLeft className="w-5 h-5 text-gray-500" />
                      </button>
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Select Classification</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{levelLabels[pickerLevel - 1]}</p>
                    </div>
                  </div>
                  <button onClick={() => setShowTaxonomyPicker(false)}>
                    <XCircle className="w-6 h-6 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>

                {/* Breadcrumbs */}
                {breadcrumbs.length > 0 && (
                  <div className="px-6 pt-4 flex items-center gap-1 text-sm shrink-0 flex-wrap">
                    {breadcrumbs.map((bc, i) => (
                      <span key={bc.level} className="flex items-center gap-1">
                        {i > 0 && <ChevronRight className="w-3 h-3 text-gray-400" />}
                        <button
                          onClick={() => {
                            setClassificationSearch('');
                            if (bc.level === 1) { setSelectedLevel2(null); setPickerLevel(2); }
                          }}
                          className="text-emerald-600 hover:text-emerald-800 font-medium truncate max-w-[200px]"
                          title={bc.label}
                        >
                          {bc.label}
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Search */}
                <div className="px-6 pt-4 shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={`Search ${levelLabels[pickerLevel - 1].toLowerCase()}…`}
                      value={classificationSearch}
                      onChange={(e) => setClassificationSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Options List */}
                <div className="p-4 flex-1 overflow-y-auto min-h-0">
                  {classificationsLoading ? (
                    <div className="flex items-center justify-center py-12 text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading classifications…
                    </div>
                  ) : options.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 text-sm">
                      {classificationSearch ? 'No matches found.' : 'No options available.'}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {options.map(opt => (
                        <button
                          key={opt.key}
                          className="w-full text-left p-3 rounded-lg hover:bg-emerald-50 border border-transparent hover:border-emerald-200 transition-colors group/opt"
                          onClick={opt.onClick}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900 group-hover/opt:text-emerald-800">{opt.label}</span>
                            {pickerLevel < 3 && <ChevronRight className="w-4 h-4 text-gray-300 group-hover/opt:text-emerald-500" />}
                          </div>
                          {opt.sublabel && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{opt.sublabel}</p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {showFunctionPicker && (() => {
          const levelLabels = ['Domain', 'Subdomain', 'Function'];
          let options: { key: string; label: string; sublabel?: string; onClick: () => void }[] = [];
          const searchLower = functionSearch.toLowerCase();

          if (functionPickerLevel === 1) {
            options = Object.keys(functionHierarchy)
              .sort((a, b) => functionHierarchy[a].domain.localeCompare(functionHierarchy[b].domain))
              .filter(dk => !searchLower || functionHierarchy[dk].domain.toLowerCase().includes(searchLower))
              .map(dk => ({
                key: dk,
                label: functionHierarchy[dk].domain,
                sublabel: `${functionHierarchy[dk].subdomains.length} subdomains`,
                onClick: () => { setSelectedFunctionDomain(dk); setFunctionPickerLevel(2); setFunctionSearch(''); },
              }));
          } else if (functionPickerLevel === 2 && selectedFunctionDomain && functionHierarchy[selectedFunctionDomain]) {
            options = [...functionHierarchy[selectedFunctionDomain].subdomains]
              .sort((a, b) => a.name.localeCompare(b.name))
              .filter(s => !searchLower || s.name.toLowerCase().includes(searchLower))
              .map(s => ({
                key: `${selectedFunctionDomain}|${s.key}`,
                label: s.name,
                sublabel: `${s.functions.length} functions`,
                onClick: () => { setSelectedFunctionSubdomain(s.key); setFunctionPickerLevel(3); setFunctionSearch(''); },
              }));
          } else if (functionPickerLevel === 3 && selectedFunctionDomain && selectedFunctionSubdomain) {
            const subdomain = functionHierarchy[selectedFunctionDomain]?.subdomains.find(s => s.key === selectedFunctionSubdomain);
            if (subdomain) {
              options = [...subdomain.functions]
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter(f => !searchLower || f.name.toLowerCase().includes(searchLower))
                .map(f => ({
                  key: `${selectedFunctionDomain}|${selectedFunctionSubdomain}|${f.key}`,
                  label: f.name,
                  onClick: () => applyFunctionClassificationFromTaxonomy(selectedFunctionDomain, selectedFunctionSubdomain, f.key),
                }));
            }
          }

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowFunctionPicker(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-gray-100 flex justify-between items-center shrink-0">
                  <div className="flex items-center gap-3">
                    {functionPickerLevel > 1 && (
                      <button
                        onClick={() => {
                          setFunctionSearch('');
                          if (functionPickerLevel === 3) {
                            setSelectedFunctionSubdomain(null);
                            setFunctionPickerLevel(2);
                          } else {
                            setSelectedFunctionDomain(null);
                            setFunctionPickerLevel(1);
                          }
                        }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <ArrowLeft className="w-5 h-5 text-gray-500" />
                      </button>
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Select Business Function</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{levelLabels[functionPickerLevel - 1]}</p>
                    </div>
                  </div>
                  <button onClick={() => setShowFunctionPicker(false)}>
                    <XCircle className="w-6 h-6 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>

                {selectedFunctionDomain && functionPickerLevel >= 2 && (
                  <div className="px-6 pt-4 flex items-center gap-1 text-sm shrink-0">
                    <span className="text-emerald-600 font-medium truncate max-w-[200px]" title={functionHierarchy[selectedFunctionDomain]?.domain ?? selectedFunctionDomain}>
                      {functionHierarchy[selectedFunctionDomain]?.domain ?? selectedFunctionDomain}
                    </span>
                    {selectedFunctionSubdomain && functionPickerLevel === 3 && (
                      <>
                        <ChevronRight className="w-3 h-3 text-gray-400" />
                        <span className="text-emerald-600 font-medium truncate max-w-[200px]">
                          {functionHierarchy[selectedFunctionDomain]?.subdomains.find(s => s.key === selectedFunctionSubdomain)?.name ?? selectedFunctionSubdomain}
                        </span>
                      </>
                    )}
                  </div>
                )}

                <div className="px-6 pt-4 shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={`Search ${levelLabels[functionPickerLevel - 1].toLowerCase()}…`}
                      value={functionSearch}
                      onChange={(e) => setFunctionSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="p-4 flex-1 overflow-y-auto min-h-0">
                  {functionClassificationsLoading ? (
                    <div className="flex items-center justify-center py-12 text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading function classifications…
                    </div>
                  ) : options.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 text-sm">
                      {functionSearch ? 'No matches found.' : 'No options available.'}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {options.map(opt => (
                        <button
                          key={opt.key}
                          className="w-full text-left p-3 rounded-lg hover:bg-emerald-50 border border-transparent hover:border-emerald-200 transition-colors group/opt"
                          onClick={opt.onClick}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900 group-hover/opt:text-emerald-800">{opt.label}</span>
                            {functionPickerLevel < 2 && <ChevronRight className="w-4 h-4 text-gray-300 group-hover/opt:text-emerald-500" />}
                          </div>
                          {opt.sublabel && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{opt.sublabel}</p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      </div>
    );
  };

  return (
    <div className="flex-1 bg-white text-gray-900 font-sans selection:bg-emerald-100 selection:text-emerald-900">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center space-x-3 group cursor-pointer" onClick={handleReset}>
              <div className={`w-10 h-10 ${BRAND_BG} rounded-xl shadow-lg shadow-emerald-200 flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300`}>
                <Database className="w-5 h-5" />
              </div>
              <div>
                <span className={`text-xl font-bold tracking-tight ${BRAND_TEXT}`}>ARA</span>
                <span className="text-xs text-gray-400 block font-medium -mt-1 tracking-widest opacity-60">Automated Records Assistant</span>
              </div>
            </div>
            <AppNavActions />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {viewState !== 'REVIEW' && (
          <div className="max-w-5xl mx-auto">{renderErrorBanner()}</div>
        )}

        {/* View Router */}
        {viewState === 'IDLE' && renderIdleState()}
        {(viewState === 'PROCESSING' || activeItem?.status === 'Processing' || activeItem?.status === 'Uploading...') && viewState !== 'REVIEW' && renderProcessingState()}
        {viewState === 'REVIEW' && renderReviewState()}
        {viewState === 'ERROR' && renderErrorState()}

        {viewState === 'IDLE' && afterReview && (
          <AfterReviewModal
            recordId={afterReview.recordId}
            discarded={afterReview.discarded}
            onClose={() => {
              clearAfterReview();
              setAfterReview(null);
            }}
          />
        )}

        {/* Recent Activity Feed (Only visible on IDLE) */}
        {viewState === 'IDLE' && (
          <div className="mt-24 border-t border-gray-100 pt-12 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                  <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingHistory ? 'animate-spin text-emerald-500' : 'text-gray-400'}`} />
                  Recent Activity
                </h3>
                <p className="text-sm text-gray-500 mt-1">Your recently processed documents.</p>
              </div>
            </div>

            {!recentActivity || recentActivity.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                <p className="text-gray-400">No recent uploads found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentActivity.map((item) => (
                  <IngestionActivityCard
                    key={item.id}
                    processId={item.id}
                    fileName={item.file?.name ?? ''}
                    status={item.status}
                    initiatedAt={item.initiated_at}
                    phase={(item as { phases?: { current_phase?: string } }).phases?.current_phase}
                  />
                ))}
              </div>
            )}
          </div>
        )}

      </main>
      {/* Footer / Status Bar (Optional) */}
      <footer className="bg-white border-t border-gray-200 mt-auto py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          <p>© 2026 Palmerston North City Council &bull; ARA</p>
        </div>
      </footer>
    </div>
  );
}
